/* ==========================================================================
   Ozeret -- interface language
   English, Spanish, Haitian Creole, Russian. Missing keys fall back to
   English, so a partial translation is always safe to ship.
   ========================================================================== */

export interface UiLang {
  id: string;
  label: string;
  native: string;
  /** Hebrew and Yiddish read right to left; the whole layout has to flip. */
  rtl?: boolean;
}

export const isRtl = (id: string): boolean =>
  UI_LANGS.find((l) => l.id === id)?.rtl === true;

export const UI_LANGS: UiLang[] = [
  { id: 'en', label: 'English',        native: 'English' },
  { id: 'es', label: 'Spanish',        native: 'Español' },
  { id: 'ht', label: 'Haitian Creole', native: 'Kreyòl' },
  { id: 'ru', label: 'Russian',        native: 'Русский' },
  { id: 'yi', label: 'Yiddish',        native: 'ייִדיש', rtl: true },
  { id: 'he', label: 'Hebrew',         native: 'עברית', rtl: true },
];

type Dict = Record<string, string>;

export const STRINGS: Record<string, Dict> = {
  en: {
    tr_busy: 'Translating…', tr_original: 'Show original', tr_translate: 'Translate',
    tr_failed: 'Could not translate just now.', tr_already: 'Already in your language',

    /* signing in */
    s_tagline: 'Housekeeping, close to home',
    s_yournumber: 'Your phone number',
    s_numberhint: 'We text you a code. Your number is never shown to anybody.',
    s_textsok: 'Text me when someone answers my listing or books me.',
    s_textsfine: 'Optional. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
    s_terms: 'Terms',
    s_privacy: 'Privacy',
    s_sendcode: 'Send me a code',
    s_onemoment: 'One moment…',
    s_thecode: 'The code we sent',
    s_codesent: 'Check your messages. It can take a minute.',
    s_codeadmin: 'Texts are not switched on yet — ask the person who invited you for your code.',
    s_whichside: 'Which are you?',
    s_ineedcleaner: 'I need a cleaner',
    s_icleanhomes: 'I clean homes',
    s_oneside: 'One number, one side. You can delete your account later and start again as the other.',

    /* Where they are, asked at sign-up. Both sides need placing before the
       account is worth showing anybody: a cleaner with no postal code and no
       areas cannot be judged or found, and a home with no address cannot be
       arrived at. */
    s_wherehome: 'Where is the home?',
    s_wherework: 'Where do you set out from?',
    s_zipplaceholder: '10952',
    s_ziphint_h: 'Cleaners nearby find you by this.',
    s_ziphint_c: 'Homes use this to tell how far you are.',
    s_address: 'Street address',
    s_addressplaceholder: '14 Maple Avenue, Apt 2',
    s_addresshint: 'Nobody sees this until you book them.',
    s_areas: 'Areas you will travel to',
    s_areasneedzip: 'Give your postal code first and the towns near you appear here.',
    s_areashint: 'Pick at least one. Homes search by place, and an empty list finds nobody.',

    /* The free month, said at sign-up instead of in week four. */
    s_howstart: 'How would you like to start?',
    s_daysfree: 'days free',
    s_subnow: 'Subscribe now',
    s_thenprice: 'then {price} per month',
    s_permonth: '{price} per month',
    s_useanyway: 'Use the address as I typed it',
    s_stillneed: 'Still needed: ',
    s_need_code: 'the code we sent you',
    s_need_side: 'whether you need a cleaner or are one',
    s_need_name: 'what to call you',
    s_need_zip: 'a five-digit postal code',
    s_need_address: 'a street address',
    s_need_areas: 'at least one area',
    s_havepromo: 'Have a code?',
    s_promoplaceholder: 'OZ4K7PQX',
    s_promohint: 'A launch code from Ozeret adds free months to your account. '
      + 'A friend’s code earns your friend a free month — it does not change your '
      + 'own free trial. Leave it blank if you do not have one.',
    s_trialhint: 'Everything works for {days} days. After that it is {price} a month, '
      + 'or carry on browsing for free. Cancel any time, in Settings.',
    s_subhint: 'You go to Stripe to pay {price} a month, and come straight back. '
      + 'Cancel any time, in Settings.',
    /* Only an admin's number reaches these: it may hold an account on each
       side, so signing in has to say which one. */
    s_whichaccount: 'Which account?',
    s_myhome: 'My home',
    s_mycleaning: 'My cleaning work',
    s_twoaccounts: 'This number has one of each. Pick the one to open — the other stays where it is.',
    s_callyou: 'What should people call you',
    s_nameplaceholder: 'The Katz family, or Marisol',
    s_namehint: 'A first name or a family name is plenty.',
    s_startusing: 'Start using Ozeret',
    s_signin: 'Sign in',
    s_wrongnumber: 'Wrong number',
    s_sendagain: 'Send it again',
    s_nodocs: 'We never ask for a social security number, immigration status, or any government document. A phone number is all it takes.',
    s_justlooking: 'Just looking?',
    s_lookaroundhint: 'Have a wander through a worked example first. Nothing you do in it is saved, and nobody else sees it.',
    s_lookhome: 'Look around as a home',
    s_lookcleaner: '…as a cleaner',
    s_disclaimer: 'Ozeret is an introduction, not an employer. We do not vet, supervise, insure or guarantee anyone, and we never hold your money.',
    s_demobanner: 'You are looking around. Nothing here is saved.',
    s_demoexit: 'Sign up for real',
    s_havecode: 'I have a sign-in code',
    s_usenumber: 'Use my phone number instead',
    s_yoursignincode: 'Your sign-in code',
    s_signincodehint: 'The permanent code from Profile → Settings on a device you are already signed in on. It does not expire.',
    h_deleteaccount: 'Delete my account',
    n_deletehint: 'Frees your number so you can start again — as a home or as a cleaner. Reviews you have written stay, because they belong to the person you wrote them about too.',
    n_deletewarn: 'This cannot be undone. Your profile, your open requests and your number go now. Type DELETE to confirm.',
    dl_why: 'Why are you leaving? (optional)', dl_nocleaners: 'No cleaners near me', dl_nohomes: 'No homes near me',
    dl_expensive: 'Too expensive', dl_looking: 'I was just looking', dl_foundhelp: 'Found help another way',
    dl_foundwork: 'Found work another way', dl_broken: 'Something didn’t work', dl_other: 'Something else',
    dl_note: 'Anything you want to tell us? It helps us fix it.',
    b_deleteaccount: 'Delete my account…',
    b_deleteforgood: 'Delete for good',

    /* the first five minutes */
    tour_title: 'A quick look round',
    tour_skip: 'Skip', tour_back: 'Back', tour_next: 'Next', tour_done: 'Got it',
    tour_again: 'Show me around again',
    tour_h1_t: 'Find', tour_h1: 'Cleaners near you, sorted by how well they fit your home. Tap one to read reviews and see when they are free.',
    tour_h2_t: 'Post', tour_h2: 'Say what you need and when. Cleaners who are free then come to you — you choose.',
    tour_h3_t: 'Requests', tour_h3: 'What you have posted, who applied, and what is booked. Anything finished waits here for a review.',
    tour_h4_t: 'Chats', tour_h4: 'Talk to a cleaner before or after booking. Your number stays private unless you share it.',
    tour_h5_t: 'My home', tour_h5: 'Your job list — the things you always want done, with a note on each. Cleaners see it only when you book them.',
    tour_c1_t: 'Work', tour_c1: 'Homes looking for help near you, best fit first. Tap a job to see the pay and the list, then say you are available.',
    tour_c2_t: 'Calendar', tour_c2: 'Mark the days and hours you can work. Homes only see you when you are free — keep it honest and the invites are real.',
    tour_c3_t: 'My jobs', tour_c3: 'What you have applied for, what is booked, and what you have finished. Start a job here on the day.',
    tour_c4_t: 'Chats', tour_c4: 'Homes message you here. Propose a price straight from the chat.',
    tour_c5_t: 'Profile', tour_c5: 'What homes see. A photo and a line about your work get you booked faster than anything else.',

    /* screens, keyed (was hard-coded English) */
    e_hit: 'This screen hit a problem',
    e_notyou: 'Not your fault — something on our side. Everything you have done is saved.',
    e_retry: 'Try again',
    e_home: 'Back to the start',
    e_what: 'What went wrong (helpful if you tell us)',
    i_keep: 'Keep Ozeret on your home screen',
    i_add: 'Add to Home Screen',
    i_opens: 'Opens like an app, without the address bar.',
    i_notnow: 'Not now',
    m_zoomin: 'Zoom in',
    m_zoomout: 'Zoom out',
    m_fit: 'Fit everything',
    p_open: 'Open photo',
    c_tapdays: 'Tap days to change them.',
    c_notimeoff: 'No time off booked.',
    c_nothingblocked: 'Nothing blocked. Use this for a school pickup or a standing appointment.',
    c_nostanding: 'No standing jobs yet.',
    c_showme: 'Show me in search',
    c_notworking: 'Not working',
    c_cleaneronly: 'Cleaner accounts only',
    c_nojobstoday: 'No jobs today',
    c_stillshowing: 'Your available hours are still showing to homes nearby.',
    j_addresslater: 'Exact address is shared once you are booked.',
    j_onevisit: 'Nothing standing — just this one visit.',
    j_regular: 'They want this to be regular.',
    j_headsup: 'Heads up.',
    j_kosher: 'Kosher home',
    j_notagreed: 'Nothing is agreed until you both confirm in the app.',
    j_share: 'Share this job',
    j_hidehome: 'Do not show me this home again',
    ip_maps: 'Open in maps',
    ip_noaddress: 'They have not filled in an address. Ask them in the chat.',
    ip_allticked: 'Everything ticked off. Finish up whenever you are ready.',
    ip_yousaid: 'You said',
    ip_pictures: 'Pictures from the home',
    ip_whathappened: 'What happened with this one?',
    w_seejob: 'See the open job',
    w_nomatch: 'Nothing matches those filters',
    w_widen: 'Try widening your areas or clearing the rate floor.',
    w_hidesugg: 'Hide these suggestions',
    w_prevdate: 'Previous date',
    w_nextdate: 'Next date',
    f_nobody: 'Nobody quite right?',
    f_postinstead: 'Post what you need and let cleaners come to you instead.',
    f_postneed: 'Post what I need',
    f_nolang: 'No shared language',
    f_invite: 'Invite to this job',
    f_nomatchall: 'Nobody matches all of that',
    f_strict: 'Filters here are strict — try removing one.',
    h_worthknowing: 'Worth knowing.',
    h_myjoblist: 'My job list',
    h_edithome: 'Edit this home',
    h_sharehome: 'Share this home by link',
    h_nonote: 'No note. Add one so a new cleaner does not have to ask.',
    h_notinlist: 'Not in the list?',
    h_addown_s: 'Add anything your home needs that the catalog does not cover. Only you see it.',
    h_sharedtag: 'Every job here is a shared tag, which is how cleaners who specialise in it find you.',
    h_filteron: 'Cleaners filter on this. It is the most common reason a good match falls apart.',
    h_pictures: 'Pictures — a cleaner working here can see these',
    h_searchjobs: 'Search jobs — oven, laundry, Shabbos…',
    h_nothingmatches: 'Nothing matches',
    h_tryword: 'Try a different word, or add your own job above.',
    h_picture: 'Picture',
    h_calledas: 'What cleaners see you called',
    h_neighborhood: 'Neighborhood',
    h_hometype: 'Home type',
    h_street: 'Street address',
    h_postal: 'Postal code',
    h_about: 'About your home',
    h_parking: 'Parking',
    h_sqft: 'Approximate square feet — ',
    pr_editing: 'Editing a live request. Everyone who already applied is told what changed.',
    pr_pickone: 'Pick at least one. Cleaners filter by these.',
    pr_tight: 'That may be tight.',
    pr_regular: 'Make this regular',
    pr_sameweek: 'Same day and time every week',
    pr_nomoney: 'Ozeret does not take or hold the money. You pay each other directly.',
    pr_start: 'Start',
    pr_howpay: 'How you will pay',
    pr_notesph: 'Parking, keys, a dog that barks, which room matters most…',
    pr_rateperhour: 'Rate per hour — ',
    r_youpay: 'You will pay',
    r_nobodycoming: 'Nobody coming',
    r_before: 'Worked with someone before Ozeret?',
    r_sharejob: 'Share this job by link',
    r_tomorrow: 'Post it for tomorrow',
    r_pickdate: 'Pick a date',
    r_stoplooking: 'Stop looking',
    r_bestfit: 'Best fit for this job',
    r_nobodyname: 'Nobody by that name. They need an Ozeret profile first — send them an invite.',
    r_searchperson: 'Search for the person who did the work. They need an Ozeret profile already.',
    r_logreview: 'Log it, then review them',
    r_prevweek: 'Previous week',
    r_nextweek: 'Next week',
    r_nothingposted: 'Nothing posted right now',
    r_invisible: 'Your home is invisible to cleaners until you post.',
    r_nofinished: 'No finished jobs yet.',
    r_noapplicants: 'No applicants yet',
    r_firsthours: 'Most requests get their first within a few hours.',
    r_typename: 'Start typing their name',
    r_searchjobs: 'Search the jobs',
    b_reviewwaiting: 'Review waiting',
    b_earned: 'Earned so far',
    b_comingup: 'Coming up',
    b_neverholds: 'Ozeret never holds your money. You are paid directly, however you and the home agree.',
    b_agreedrate: 'Agreed rate',
    b_paidby: 'Paid by',
    b_afterfact: 'Recorded after the fact — this job happened before Ozeret.',
    b_justthistime: 'Just this time',
    b_record: 'This is the written record of what was agreed.',
    b_arrived: 'I have arrived — begin',
    b_openslist: 'Opens the list with their notes, so you can tick jobs off as you go.',
    b_cancelbooking: 'Cancel this booking',
    b_youwrote: 'You wrote',
    b_hidden14: 'Reviews stay hidden until you both write one, or 14 days pass.',
    b_nothingbooked: 'Nothing booked yet.',
    b_nothingwaiting: 'Nothing waiting',
    b_appliedhere: 'Jobs you have applied to show here until the home decides.',
    cp_replytime: 'Typical reply time',
    cp_calconfirmed: 'Calendar last confirmed',
    cp_phoneverified: 'Phone verified',
    cp_references: 'References on file',
    cp_shareprofile: 'Share my profile by link',
    cp_reportcal: 'Report a calendar problem',
    cp_yourpicture: 'Your picture',
    cp_oneline: 'One line about your work',
    cp_longer: 'Longer description',
    cp_youare: 'You are',
    cp_minhours: 'Minimum hours',
    cp_years: 'Years doing this',
    cp_startfrom: 'Where you start from',
    cp_howfar: 'How far you will travel — ',
    mg_numbershidden: 'Numbers stay hidden until you both choose to share them.',
    mg_private: 'Your number stays private unless you share it.',
    mg_sharenumber: 'Share my number',
    mg_tapremove: 'Tap a picture to take it off this message.',
    mg_noconv: 'No conversations yet.',
    mg_removepic: 'Remove this picture',
    mg_sendpic: 'Send a picture',
    mg_write: 'Write a message',
    st_turnoff: 'Turn this off and homes cannot find you, without touching your calendar.',
    st_signedin: 'Signed in as',
    st_housekey: 'Treat it like a house key.',
    st_includescreen: 'We will include which screen you were on before you tapped feedback.',
    st_whathappened: 'What happened, what you expected, what you wanted instead',
    sh_tagline: 'Housekeeping, arranged between people who live near each other.',
    sh_gone: 'This one is no longer available.',
    sh_supplies: 'Supplies provided',
    sh_nothingopen: 'Nothing open at the moment.',
    sx_oneweek: 'This changes this one week. Every other week carries on exactly as it is.',
    sx_differs: 'This week already differs from the usual list.',
    sx_seeschange: 'They will see the change in your conversation, so nothing moves under them.',
    sx_nottold: 'They are not told.',
    sx_perproperty: 'Job lists, requests and ratings are per property.',
    sx_addthis: 'Add this property',
    sx_addanother: 'Add another property',
    sx_theywouldpay: 'They would pay',
    sx_onlyinvited: 'You can only report a day you actually invited them for.',
    sx_notesshown: 'Notes are shown to a cleaner only once you have ticked this job in a live request.',
    sx_listweek: 'On the list this week',
    sx_addweek: 'Add something, this week only',
    sx_sayweek: 'Anything to say about this week',
    sx_awayph: 'We will be away — the key is with next door.',
    sx_listtime: 'On the list this time',
    sx_addtime: 'Add something else, this time only',
    sx_sayvisit: 'Anything to say about this visit',
    sx_dustph: 'The builders left dust in the back room — please do that first.',
    sx_picsvisit: 'Pictures for this visit',
    sx_callit: 'What you will call it',
    sx_callitph: 'The Lakewood house, Mum\'s place, the office',
    sx_homeorjob: 'A home, or a job',
    sx_homespeaks: 'Home speaks',
    sx_searchname: 'Search by name',
    sx_cleanername: 'A cleaner\'s name',
    sx_freeon: 'Free on',
    sx_worksin: 'Works in', sx_allareas: 'Show everywhere else', sx_nearonly: 'Just nearby',
    sx_speaks: 'Speaks',
    sx_cleaneris: 'Cleaner is',
    sx_ratedatleast: 'Rated at least',
    sx_howtheywork: 'How they work',
    sx_showfreeon: 'Show cleaners free on',
    sx_saywhy: 'Say why (optional)',
    sx_addnote: 'Add a note',
    sx_optional: 'Optional',
    sx_whichday: 'Which day',
    sx_whathappened: 'What happened',
    sx_anythingadd: 'Anything to add',
    sx_yournote: 'Your note for this job',
    sx_toldonce: 'Anything a new cleaner would need to be told once',
    sx_thesedays: 'These days are',
    sx_blocked: 'Blocked from / until',
    sx_schoolph: 'School pickup, appointment…',
    sx_awayph2: 'Away, family, sick…',
    sx_workinghours: 'Working hours',
    sx_whatcalled: 'What is it called',
    sx_fishtankph: 'Wipe down the fish tank',
    sx_shouldknow: 'Anything they should know',
    sx_awayph3: 'Away, family, surgery…',
    sx_stopseeing: 'Stop seeing ',
    sx_payatleast: 'Pay at least — ',
    sx_yourrate: 'Your rate — ',

    now: 'Now', cleaners_n: 'cleaners', freefrom: 'free from', availableon: 'available',
    woman: 'Woman', man: 'Man', partofday: 'part of the day', covers: 'Covers', speaks: 'Speaks',
    worksin: 'Works in',
    /* Two sides, two sentences. The old single string was written from the
       cleaner's chair -- "of their jobs are your specialty" -- and a household
       reading it about a cleaner was told it had a specialty, which it does
       not. Same score, same fact, said from the reader's side. */
    jobsspecialty: 'of their jobs are your specialty',
    jobsspecialty_h: 'of your jobs are their specialty',
    jobsspecialty1: 'of their jobs is your specialty', jobsspecialty1_h: 'of your jobs is their specialty',
    paysatorabove_h: 'You pay at or above their rate', belowusual_h: 'Below their usual', shorterthan_h: 'Shorter than their minimum of',
    bothspeak: 'You both speak',
    paysatorabove: 'Pays at or above your rate', belowusual: 'Below your usual', shorterthan: 'Shorter than your minimum of', kosherexp: 'Kosher-home experienced',

    st_codehow: 'Open Ozeret on your other phone or laptop, choose "I have a sign-in code", and enter this. It does not expire and you can use it as often as you like.',
    st_showcode: 'Show my code', st_hidecode: 'Hide', st_copy: 'Copy', st_copied: 'Copied',
    st_keywarn: 'Anyone with this code can sign in as you and see your bookings and messages. Do not put it in a group chat.',

    /* tabs */
    tab_work: 'Work', tab_calendar: 'Calendar', tab_myjobs: 'My jobs', tab_chats: 'Chats',
    tab_profile: 'Profile', tab_find: 'Find', tab_requests: 'Requests', tab_post: 'Post',
    tab_myhome: 'My home',

    /* screen titles */
    t_findwork: 'Find work', t_thejob: 'The job', t_mycalendar: 'My calendar',
    t_myjobs: 'My jobs', t_booking: 'Booking', t_messages: 'Messages', t_myprofile: 'My profile',
    t_findcleaner: 'Find a cleaner', t_profile: 'Profile', t_whatneed: 'What do you need?',
    t_myrequests: 'My requests', t_applicants: 'Applicants', t_myjoblist: 'Jobs in my home',
    t_myhome: 'My home', t_updates: 'Updates', t_review: 'Write a review',
    t_editprofile: 'Edit profile', t_feedback: 'Send feedback', t_editrequest: 'Edit request',
    t_pastjob: 'Log a past job', t_settings: 'Settings', t_inprogress: 'The job',

    /* buttons */
    b_apply: "I'm available", b_offer: 'Offer my rate', b_message: 'Message', b_withdraw: 'Withdraw',
    b_save: 'Save', b_cancel: 'Cancel', b_close: 'Close', b_show: 'Show results', b_clear: 'Clear all',
    b_edit: 'Edit', b_remove: 'Remove', b_add: 'Add', b_send: 'Send', b_choose: 'Choose',
    b_post: 'Post it — go live to cleaners', b_block: 'Save these days', b_submit: 'Submit review',
    b_finish: 'Mark finished', b_again: 'Book again', b_signout: 'Sign out', b_saveprofile: 'Save profile',
    b_negotiate: 'Propose a price', b_accept: 'Accept', b_decline: 'Decline', b_invite: 'Invite',

    /* common */
    perhour: '/hr', hours: 'hours', total: 'Total', when: 'When', time: 'Time', pay: 'Pay',
    onetime: 'One time', everyweek: 'Every week', applied: 'applied', reviews: 'reviews',
    match: 'match', available: 'Available', unavailable: 'Unavailable', booked: 'Booked',
    partly: 'Part day', today: 'Today', tomorrow: 'Tomorrow', thisweek: 'This week',
    upcoming: 'Upcoming', finished: 'Finished',
    h_needsfinishing: 'Needs finishing',
    n_needsfinishing: 'The day has passed and nobody has said whether it happened. Open each one and mark it finished — or cancel it if it never did.',
    n_needsfinishing_c: 'The day has passed. Open each one and mark it finished so the home can review you and settle up.',
    n_nothingbooked: 'Nothing booked.', languages: 'Languages', specialties: 'Specialties',
    areas: 'Areas covered', rate: 'Rate', minimum: 'minimum', years: 'years',
    filters: 'Filters', sortby: 'Sort by', list: 'List', map: 'Map', week: 'Week', month: 'Month',
    nothing: 'Nothing here yet', anyday: 'Any day', pickdate: 'Pick a date',
    cheapest: 'Lowest rate', dearest: 'Highest rate', bestrated: 'Best rated', bestmatch: 'Best match',
    soonest: 'Soonest', mosthours: 'Most hours', nearby: 'Nearby',
    schedule: 'Schedule', earnings: 'Earnings', timeoff: 'Time off', usualweek: 'Your usual week',
    allday: 'All day', from: 'From', to: 'To', reason: 'Reason', note: 'Note',
    applied_tab: 'Applied',

    /* section headings inside screens */
    h_aboutyou: 'About you',
    h_account: 'Account',
    h_addcatalog: 'Add from the catalog',
    h_addown: 'Add your own job',
    h_agreedlist: 'Agreed job list',
    h_alreadysent: 'Already sent',
    h_anythingelse: 'Anything else',
    h_basics: 'Basics',
    h_beingfound: 'Being found',
    h_blocked: 'Blocked hours',
    h_bookagain: 'Would you book them again',
    h_fittogether: 'Jobs that fit together',
    h_howcleanersrate: 'How cleaners rate you',
    h_howtheyrated: 'How they are rated',
    h_howyouhear: 'How you hear from us',
    h_howyouwork: 'How you work',
    h_inyourwords: 'In your words',
    h_jobsinhome: 'Jobs in this home',
    h_justthistime: 'Just this time',
    h_langshere: 'Languages spoken here',
    h_language: 'Language',
    h_notshown: 'Not shown to you',
    h_opentoday: 'Open today near you',
    h_otherdevice: 'Using Ozeret on another device',
    h_overall: 'Overall',
    h_practical: 'Practical',
    h_progress: 'Progress',
    h_rateandhours: 'Rate and hours',
    h_reliability: 'Reliability',
    h_reportcal: 'Report a calendar problem',
    h_reviews: 'Reviews',
    h_size: 'Size',
    h_standing: 'Standing jobs',
    h_tellus: 'Tell us',
    h_thehome: 'The home',
    h_thelist: 'The list',
    h_thisvisit: 'This visit',
    h_whatabout: 'What is it about',
    h_whatcleanerssay: 'What cleaners say about them',
    h_whatcleanerswrote: 'What cleaners wrote',
    h_whattheydid: 'What they did',
    h_whattheyneed: 'What they need done',
    h_whatwasdone: 'What was done',
    h_where: 'Where',
    h_wherereach: 'Where we reach you',
    h_whichjobs: 'Which jobs this time',
    h_whichproperty: 'Which property',
    h_who: 'Who',
    h_whyshown: 'Why we showed you this',
    h_yourhome: 'Your home',
    h_yourprops: 'Your properties',
    h_yourrequest: 'Your request',
    h_intheirwords: 'In their words',
    h_howtheywork: 'How they work',
    t_admin: 'Dashboard', t_sql: 'SQL',
  },

  es: {
    tr_busy: 'Traduciendo…', tr_original: 'Ver original', tr_translate: 'Traducir',
    tr_failed: 'No se pudo traducir ahora mismo.', tr_already: 'Ya está en su idioma',

    /* signing in */
    s_tagline: 'Limpieza del hogar, cerca de casa',
    s_yournumber: 'Su número de teléfono',

    s_wherehome: '¿Dónde está la casa?',
    s_wherework: '¿Desde dónde sale usted?',
    s_zipplaceholder: '10952',
    s_ziphint_h: 'Las limpiadoras cercanas la encuentran por esto.',
    s_ziphint_c: 'Las casas usan esto para saber a qué distancia está.',
    s_address: 'Dirección',
    s_addressplaceholder: '14 Maple Avenue, Apt 2',
    s_addresshint: 'Nadie la ve hasta que usted la contrate.',
    s_areas: 'Zonas a las que viajará',
    s_areasneedzip: 'Ponga su código postal primero y aquí aparecen los pueblos cercanos.',
    s_areashint: 'Elija al menos una. Las casas buscan por zona, y una lista vacía no encuentra a nadie.',
    s_howstart: '¿Cómo quiere empezar?',
    s_daysfree: 'días gratis',
    s_subnow: 'Suscribirse ahora',
    s_thenprice: 'luego {price} al mes',
    s_permonth: '{price} al mes',
    s_useanyway: 'Usar la dirección tal como la escribí',
    s_stillneed: 'Todavía falta: ',
    s_need_code: 'el código que le enviamos',
    s_need_side: 'si necesita limpieza o la ofrece',
    s_need_name: 'cómo llamarle',
    s_need_zip: 'un código postal de cinco dígitos',
    s_need_address: 'una dirección',
    s_need_areas: 'al menos una zona',
    s_havepromo: '¿Tiene un código?',
    s_promoplaceholder: 'OZ4K7PQX',
    s_promohint: 'Un código de lanzamiento de Ozeret añade meses gratis a su cuenta. '
      + 'El código de un amigo le da a su amigo un mes gratis — su propia prueba no '
      + 'cambia. Déjelo vacío si no tiene uno.',
    s_trialhint: 'Todo funciona durante {days} días. Después son {price} al mes, '
      + 'o siga mirando gratis. Cancele cuando quiera, en Ajustes.',
    s_subhint: 'Va a Stripe para pagar {price} al mes, y vuelve enseguida. '
      + 'Cancele cuando quiera, en Ajustes.',
    s_numberhint: 'Le enviamos un código por mensaje. Su número nunca se muestra a nadie.',
    s_textsok: 'Envíenme un mensaje cuando alguien responda a mi anuncio o me contrate.',
    s_textsfine: 'Opcional. La frecuencia varía. Pueden aplicar tarifas de mensajes y datos. Responda STOP para cancelar, HELP para ayuda.',
    s_terms: 'Términos',
    s_privacy: 'Privacidad',
    s_sendcode: 'Enviarme un código',
    s_onemoment: 'Un momento…',
    s_thecode: 'El código que le enviamos',
    s_codesent: 'Revise sus mensajes. Puede tardar un minuto.',
    s_codeadmin: 'Los mensajes de texto aún no están activados. Pida su código a la persona que le invitó.',
    s_whichside: '¿Cuál es usted?',
    s_ineedcleaner: 'Necesito limpieza',
    s_icleanhomes: 'Yo limpio casas',
    s_oneside: 'Un número, un lado. Puede borrar su cuenta más adelante y empezar de nuevo como el otro.',
    s_whichaccount: '¿Cuál cuenta?',
    s_myhome: 'Mi casa',
    s_mycleaning: 'Mi trabajo de limpieza',
    s_twoaccounts: 'Este número tiene una de cada. Elija cuál abrir; la otra se queda como está.',
    s_callyou: '¿Cómo quiere que le llamen?',
    s_nameplaceholder: 'La familia Katz, o Marisol',
    s_namehint: 'Con un nombre o un apellido basta.',
    s_startusing: 'Empezar a usar Ozeret',
    s_signin: 'Entrar',
    s_wrongnumber: 'Número equivocado',
    s_sendagain: 'Enviar de nuevo',
    s_nodocs: 'Nunca pedimos número de seguro social, estatus migratorio ni ningún documento del gobierno. Solo hace falta un número de teléfono.',
    s_justlooking: '¿Solo quiere ver?',
    s_lookaroundhint: 'Dé una vuelta por un ejemplo primero. Nada de lo que haga ahí se guarda, y nadie más lo ve.',
    s_lookhome: 'Ver como casa',
    s_lookcleaner: '…como limpiador/a',
    s_disclaimer: 'Ozeret es una presentación, no un empleador. No verificamos, supervisamos, aseguramos ni garantizamos a nadie, y nunca guardamos su dinero.',
    s_demobanner: 'Está mirando un ejemplo. Nada de esto se guarda.',
    s_demoexit: 'Registrarse de verdad',
    s_havecode: 'Tengo un código de acceso',
    s_usenumber: 'Usar mi número de teléfono',
    s_yoursignincode: 'Su código de acceso',
    s_signincodehint: 'El código permanente que aparece en Perfil → Ajustes en un dispositivo donde ya inició sesión. No caduca.',
    h_deleteaccount: 'Borrar mi cuenta',
    n_deletehint: 'Libera su número para poder empezar de nuevo, como casa o como limpiador/a. Las reseñas que escribió se quedan, porque también pertenecen a la persona sobre la que las escribió.',
    n_deletewarn: 'Esto no se puede deshacer. Su perfil, sus solicitudes abiertas y su número se van ahora. Escriba DELETE para confirmar.',
    dl_why: '¿Por qué se va? (opcional)', dl_nocleaners: 'No hay personas que limpien cerca', dl_nohomes: 'No hay casas cerca',
    dl_expensive: 'Es muy caro', dl_looking: 'Solo estaba mirando', dl_foundhelp: 'Encontré ayuda de otra forma',
    dl_foundwork: 'Encontré trabajo de otra forma', dl_broken: 'Algo no funcionó', dl_other: 'Otra razón',
    dl_note: '¿Algo que quiera decirnos? Nos ayuda a mejorar.',
    b_deleteaccount: 'Borrar mi cuenta…',
    b_deleteforgood: 'Borrar definitivamente',

    /* the first five minutes */
    tour_title: 'Un vistazo rápido',
    tour_skip: 'Saltar', tour_back: 'Atrás', tour_next: 'Siguiente', tour_done: 'Entendido',
    tour_again: 'Mostrarme el recorrido otra vez',
    tour_h1_t: 'Buscar', tour_h1: 'Limpiadores cerca de usted, ordenados por lo bien que encajan con su casa. Toque uno para leer reseñas y ver cuándo está libre.',
    tour_h2_t: 'Publicar', tour_h2: 'Diga qué necesita y cuándo. Los limpiadores libres a esa hora vienen a usted; usted elige.',
    tour_h3_t: 'Solicitudes', tour_h3: 'Lo que publicó, quién se postuló y qué está reservado. Lo terminado espera aquí una reseña.',
    tour_h4_t: 'Mensajes', tour_h4: 'Hable con un limpiador antes o después de reservar. Su número queda privado a menos que lo comparta.',
    tour_h5_t: 'Mi casa', tour_h5: 'Su lista de tareas: lo que siempre quiere que se haga, con una nota en cada una. Los limpiadores la ven solo cuando usted los reserva.',
    tour_c1_t: 'Trabajo', tour_c1: 'Casas que buscan ayuda cerca de usted, las que mejor encajan primero. Toque un trabajo para ver el pago y la lista, y diga que está disponible.',
    tour_c2_t: 'Calendario', tour_c2: 'Marque los días y horas en que puede trabajar. Las casas solo le ven cuando está libre; sea honesto/a y las invitaciones serán reales.',
    tour_c3_t: 'Mis trabajos', tour_c3: 'A qué se postuló, qué está reservado y qué terminó. Empiece un trabajo aquí el mismo día.',
    tour_c4_t: 'Mensajes', tour_c4: 'Las casas le escriben aquí. Proponga un precio directamente desde el chat.',
    tour_c5_t: 'Perfil', tour_c5: 'Lo que ven las casas. Una foto y una línea sobre su trabajo consiguen reservas más rápido que cualquier otra cosa.',

    /* screens, keyed (was hard-coded English) */
    e_hit: 'Esta pantalla tuvo un problema',
    e_notyou: 'No es culpa suya; algo de nuestro lado. Todo lo que hizo está guardado.',
    e_retry: 'Intentar de nuevo',
    e_home: 'Volver al inicio',
    e_what: 'Qué falló (nos ayuda si nos lo dice)',
    i_keep: 'Tenga Ozeret en su pantalla de inicio',
    i_add: 'Añadir a pantalla de inicio',
    i_opens: 'Se abre como una app, sin la barra de dirección.',
    i_notnow: 'Ahora no',
    m_zoomin: 'Acercar',
    m_zoomout: 'Alejar',
    m_fit: 'Ver todo',
    p_open: 'Abrir foto',
    c_tapdays: 'Toque los días para cambiarlos.',
    c_notimeoff: 'No hay días libres marcados.',
    c_nothingblocked: 'Nada bloqueado. Úselo para recoger a los niños o una cita fija.',
    c_nostanding: 'Aún no hay trabajos fijos.',
    c_showme: 'Mostrarme en las búsquedas',
    c_notworking: 'No trabajo',
    c_cleaneronly: 'Solo cuentas de limpiador/a',
    c_nojobstoday: 'Hoy no hay trabajos',
    c_stillshowing: 'Sus horas disponibles siguen visibles para las casas cercanas.',
    j_addresslater: 'La dirección exacta se comparte cuando le reservan.',
    j_onevisit: 'Nada fijo: solo esta visita.',
    j_regular: 'Quieren que sea regular.',
    j_headsup: 'Atención.',
    j_kosher: 'Casa kosher',
    j_notagreed: 'Nada queda acordado hasta que ambos confirmen en la app.',
    j_share: 'Compartir este trabajo',
    j_hidehome: 'No mostrarme esta casa otra vez',
    ip_maps: 'Abrir en mapas',
    ip_noaddress: 'No han puesto una dirección. Pregúnteles en el chat.',
    ip_allticked: 'Todo marcado. Termine cuando esté listo/a.',
    ip_yousaid: 'Usted dijo',
    ip_pictures: 'Fotos de la casa',
    ip_whathappened: '¿Qué pasó con esta?',
    w_seejob: 'Ver el trabajo abierto',
    w_nomatch: 'Nada coincide con esos filtros',
    w_widen: 'Amplíe sus zonas o quite el mínimo de pago.',
    w_hidesugg: 'Ocultar estas sugerencias',
    w_prevdate: 'Fecha anterior',
    w_nextdate: 'Fecha siguiente',
    f_nobody: '¿Nadie le convence?',
    f_postinstead: 'Publique lo que necesita y deje que los limpiadores vengan a usted.',
    f_postneed: 'Publicar lo que necesito',
    f_nolang: 'Sin idioma en común',
    f_invite: 'Invitar a este trabajo',
    f_nomatchall: 'Nadie cumple todo eso',
    f_strict: 'Los filtros aquí son estrictos; pruebe quitando uno.',
    h_worthknowing: 'Conviene saberlo.',
    h_myjoblist: 'Mi lista de tareas',
    h_edithome: 'Editar esta casa',
    h_sharehome: 'Compartir esta casa por enlace',
    h_nonote: 'Sin nota. Añada una para que un limpiador nuevo no tenga que preguntar.',
    h_notinlist: '¿No está en la lista?',
    h_addown_s: 'Añada lo que su casa necesite y el catálogo no cubra. Solo usted lo ve.',
    h_sharedtag: 'Cada tarea aquí es una etiqueta compartida; así le encuentran los limpiadores que se especializan en ella.',
    h_filteron: 'Los limpiadores filtran por esto. Es la razón más común por la que una buena coincidencia se cae.',
    h_pictures: 'Fotos: un limpiador que trabaje aquí puede verlas',
    h_searchjobs: 'Buscar tareas: horno, lavandería, Shabbos…',
    h_nothingmatches: 'Nada coincide',
    h_tryword: 'Pruebe otra palabra, o añada su propia tarea arriba.',
    h_picture: 'Foto',
    h_calledas: 'Cómo le ven los limpiadores',
    h_neighborhood: 'Vecindario',
    h_hometype: 'Tipo de vivienda',
    h_street: 'Dirección',
    h_postal: 'Código postal',
    h_about: 'Sobre su casa',
    h_parking: 'Estacionamiento',
    h_sqft: 'Pies cuadrados aproximados — ',
    pr_editing: 'Está editando una solicitud activa. A quienes ya se postularon se les avisa del cambio.',
    pr_pickone: 'Elija al menos una. Los limpiadores filtran por esto.',
    pr_tight: 'Puede quedar justo.',
    pr_regular: 'Hacerlo regular',
    pr_sameweek: 'El mismo día y hora cada semana',
    pr_nomoney: 'Ozeret no cobra ni guarda el dinero. Se pagan directamente entre ustedes.',
    pr_start: 'Inicio',
    pr_howpay: 'Cómo va a pagar',
    pr_notesph: 'Estacionamiento, llaves, un perro que ladra, qué habitación importa más…',
    pr_rateperhour: 'Tarifa por hora — ',
    r_youpay: 'Usted pagará',
    r_nobodycoming: 'No viene nadie',
    r_before: '¿Trabajó con alguien antes de Ozeret?',
    r_sharejob: 'Compartir este trabajo por enlace',
    r_tomorrow: 'Publicarlo para mañana',
    r_pickdate: 'Elegir una fecha',
    r_stoplooking: 'Dejar de buscar',
    r_bestfit: 'Mejor opción para este trabajo',
    r_nobodyname: 'Nadie con ese nombre. Primero necesitan un perfil en Ozeret; envíeles una invitación.',
    r_searchperson: 'Busque a la persona que hizo el trabajo. Ya debe tener un perfil en Ozeret.',
    r_logreview: 'Registrarlo y luego calificar',
    r_prevweek: 'Semana anterior',
    r_nextweek: 'Semana siguiente',
    r_nothingposted: 'Nada publicado ahora mismo',
    r_invisible: 'Su casa es invisible para los limpiadores hasta que publique.',
    r_nofinished: 'Aún no hay trabajos terminados.',
    r_noapplicants: 'Aún no hay postulantes',
    r_firsthours: 'La mayoría de las solicitudes reciben el primero en pocas horas.',
    r_typename: 'Empiece a escribir su nombre',
    r_searchjobs: 'Buscar en las tareas',
    b_reviewwaiting: 'Reseña pendiente',
    b_earned: 'Ganado hasta ahora',
    b_comingup: 'Próximo',
    b_neverholds: 'Ozeret nunca guarda su dinero. Le pagan directamente, como acuerden usted y la casa.',
    b_agreedrate: 'Tarifa acordada',
    b_paidby: 'Pago por',
    b_afterfact: 'Registrado después: este trabajo ocurrió antes de Ozeret.',
    b_justthistime: 'Solo esta vez',
    b_record: 'Este es el registro escrito de lo acordado.',
    b_arrived: 'Ya llegué — empezar',
    b_openslist: 'Abre la lista con sus notas, para ir marcando tareas.',
    b_cancelbooking: 'Cancelar esta reserva',
    b_youwrote: 'Usted escribió',
    b_hidden14: 'Las reseñas se ocultan hasta que ambos escriban una, o pasen 14 días.',
    b_nothingbooked: 'Aún nada reservado.',
    b_nothingwaiting: 'Nada pendiente',
    b_appliedhere: 'Los trabajos a los que se postuló aparecen aquí hasta que la casa decida.',
    cp_replytime: 'Tiempo típico de respuesta',
    cp_calconfirmed: 'Calendario confirmado por última vez',
    cp_phoneverified: 'Teléfono verificado',
    cp_references: 'Referencias registradas',
    cp_shareprofile: 'Compartir mi perfil por enlace',
    cp_reportcal: 'Reportar un problema de calendario',
    cp_yourpicture: 'Su foto',
    cp_oneline: 'Una línea sobre su trabajo',
    cp_longer: 'Descripción más larga',
    cp_youare: 'Usted es',
    cp_minhours: 'Horas mínimas',
    cp_years: 'Años haciendo esto',
    cp_startfrom: 'Desde dónde sale',
    cp_howfar: 'Hasta dónde viaja — ',
    mg_numbershidden: 'Los números quedan ocultos hasta que ambos decidan compartirlos.',
    mg_private: 'Su número queda privado a menos que lo comparta.',
    mg_sharenumber: 'Compartir mi número',
    mg_tapremove: 'Toque una foto para quitarla de este mensaje.',
    mg_noconv: 'Aún no hay conversaciones.',
    mg_removepic: 'Quitar esta foto',
    mg_sendpic: 'Enviar una foto',
    mg_write: 'Escriba un mensaje',
    st_turnoff: 'Si lo apaga, las casas no pueden encontrarle, sin tocar su calendario.',
    st_signedin: 'Sesión iniciada como',
    st_housekey: 'Trátelo como la llave de su casa.',
    st_includescreen: 'Incluiremos en qué pantalla estaba antes de tocar comentarios.',
    st_whathappened: 'Qué pasó, qué esperaba, qué quería en su lugar',
    sh_tagline: 'Limpieza del hogar, acordada entre personas que viven cerca.',
    sh_gone: 'Este ya no está disponible.',
    sh_supplies: 'Productos incluidos',
    sh_nothingopen: 'Nada abierto por el momento.',
    sx_oneweek: 'Esto cambia solo esta semana. Las demás siguen exactamente igual.',
    sx_differs: 'Esta semana ya difiere de la lista habitual.',
    sx_seeschange: 'Verán el cambio en su conversación, así nada les cambia por sorpresa.',
    sx_nottold: 'No se les avisa.',
    sx_perproperty: 'Las listas de tareas, solicitudes y calificaciones son por propiedad.',
    sx_addthis: 'Añadir esta propiedad',
    sx_addanother: 'Añadir otra propiedad',
    sx_theywouldpay: 'Pagarían',
    sx_onlyinvited: 'Solo puede reportar un día para el que realmente les invitó.',
    sx_notesshown: 'Las notas se muestran al limpiador solo cuando marca esta tarea en una solicitud activa.',
    sx_listweek: 'En la lista esta semana',
    sx_addweek: 'Añadir algo, solo esta semana',
    sx_sayweek: 'Algo que decir sobre esta semana',
    sx_awayph: 'Estaremos fuera; la llave la tiene el vecino.',
    sx_listtime: 'En la lista esta vez',
    sx_addtime: 'Añadir algo más, solo esta vez',
    sx_sayvisit: 'Algo que decir sobre esta visita',
    sx_dustph: 'Los obreros dejaron polvo en el cuarto de atrás; por favor empiece por ahí.',
    sx_picsvisit: 'Fotos para esta visita',
    sx_callit: 'Cómo la va a llamar',
    sx_callitph: 'La casa de Lakewood, la de mamá, la oficina',
    sx_homeorjob: 'Una casa, o una tarea',
    sx_homespeaks: 'La casa habla',
    sx_searchname: 'Buscar por nombre',
    sx_cleanername: 'Nombre de un limpiador',
    sx_freeon: 'Libre el',
    sx_worksin: 'Trabaja en', sx_allareas: 'Ver las demás zonas', sx_nearonly: 'Solo cercanas',
    sx_speaks: 'Habla',
    sx_cleaneris: 'El limpiador es',
    sx_ratedatleast: 'Calificado al menos',
    sx_howtheywork: 'Cómo trabajan',
    sx_showfreeon: 'Mostrar limpiadores libres el',
    sx_saywhy: 'Diga por qué (opcional)',
    sx_addnote: 'Añadir una nota',
    sx_optional: 'Opcional',
    sx_whichday: 'Qué día',
    sx_whathappened: 'Qué pasó',
    sx_anythingadd: 'Algo que añadir',
    sx_yournote: 'Su nota para esta tarea',
    sx_toldonce: 'Lo que habría que decirle una vez a un limpiador nuevo',
    sx_thesedays: 'Estos días son',
    sx_blocked: 'Bloqueado de / hasta',
    sx_schoolph: 'Recoger a los niños, cita…',
    sx_awayph2: 'Fuera, familia, enfermo/a…',
    sx_workinghours: 'Horario de trabajo',
    sx_whatcalled: 'Cómo se llama',
    sx_fishtankph: 'Limpiar la pecera',
    sx_shouldknow: 'Algo que deban saber',
    sx_awayph3: 'Fuera, familia, cirugía…',
    sx_stopseeing: 'Dejar de ver ',
    sx_payatleast: 'Pagar al menos — ',
    sx_yourrate: 'Su tarifa — ',

    now: 'Ahora', cleaners_n: 'limpiadores', freefrom: 'libres desde', availableon: 'disponibles el',
    woman: 'Mujer', man: 'Hombre', partofday: 'parte del día', covers: 'Cubre', speaks: 'Habla',
    worksin: 'Trabaja en',
    jobsspecialty: 'de sus tareas son su especialidad',
    jobsspecialty_h: 'de sus tareas son la especialidad de esta persona',
    bothspeak: 'Ambos hablan',
    paysatorabove: 'Paga su tarifa o más', belowusual: 'Por debajo de su habitual', shorterthan: 'Menos que su mínimo de', kosherexp: 'Con experiencia en casas kosher',

    st_codehow: 'Abra Ozeret en su otro teléfono o computadora, elija "Tengo un código de acceso" y escriba este. No caduca y puede usarlo cuantas veces quiera.',
    st_showcode: 'Ver mi código', st_hidecode: 'Ocultar', st_copy: 'Copiar', st_copied: 'Copiado',
    st_keywarn: 'Cualquiera con este código puede entrar como usted y ver sus reservas y mensajes. No lo ponga en un chat de grupo.',

    tab_work: 'Trabajo', tab_calendar: 'Calendario', tab_myjobs: 'Mis trabajos', tab_chats: 'Mensajes',
    tab_profile: 'Perfil', tab_find: 'Buscar', tab_requests: 'Solicitudes', tab_post: 'Publicar',
    tab_myhome: 'Mi casa',

    t_findwork: 'Buscar trabajo', t_thejob: 'El trabajo', t_mycalendar: 'Mi calendario',
    t_myjobs: 'Mis trabajos', t_booking: 'Reserva', t_messages: 'Mensajes', t_myprofile: 'Mi perfil',
    t_findcleaner: 'Buscar limpiador', t_profile: 'Perfil', t_whatneed: '¿Qué necesita?',
    t_myrequests: 'Mis solicitudes', t_applicants: 'Candidatos', t_myjoblist: 'Tareas de mi casa',
    t_myhome: 'Mi casa', t_updates: 'Novedades', t_review: 'Escribir una reseña',
    t_editprofile: 'Editar perfil', t_feedback: 'Enviar comentarios', t_editrequest: 'Editar solicitud',
    t_pastjob: 'Registrar trabajo anterior', t_settings: 'Ajustes', t_inprogress: 'El trabajo',

    b_apply: 'Estoy disponible', b_offer: 'Ofrecer mi tarifa', b_message: 'Mensaje', b_withdraw: 'Retirar',
    b_save: 'Guardar', b_cancel: 'Cancelar', b_close: 'Cerrar', b_show: 'Ver resultados', b_clear: 'Borrar todo',
    b_edit: 'Editar', b_remove: 'Quitar', b_add: 'Añadir', b_send: 'Enviar', b_choose: 'Elegir',
    b_post: 'Publicar — visible para limpiadores', b_block: 'Guardar estos días', b_submit: 'Enviar reseña',
    b_finish: 'Marcar terminado', b_again: 'Reservar otra vez', b_signout: 'Cerrar sesión', b_saveprofile: 'Guardar perfil',
    b_negotiate: 'Proponer un precio', b_accept: 'Aceptar', b_decline: 'Rechazar', b_invite: 'Invitar',

    perhour: '/hora', hours: 'horas', total: 'Total', when: 'Cuándo', time: 'Hora', pay: 'Pago',
    onetime: 'Una vez', everyweek: 'Cada semana', applied: 'se postularon', reviews: 'reseñas',
    match: 'compatible', available: 'Disponible', unavailable: 'No disponible', booked: 'Reservado',
    partly: 'Medio día', today: 'Hoy', tomorrow: 'Mañana', thisweek: 'Esta semana',
    upcoming: 'Próximos', finished: 'Terminados',
    h_needsfinishing: 'Falta cerrar',
    n_needsfinishing: 'El día ya pasó y nadie ha dicho si se hizo. Abra cada uno y márquelo como terminado, o cancélelo si no se hizo.',
    n_needsfinishing_c: 'El día ya pasó. Abra cada uno y márquelo como terminado para que la casa pueda calificarle y pagar.',
    n_nothingbooked: 'Nada reservado.', languages: 'Idiomas', specialties: 'Especialidades',
    areas: 'Zonas que cubre', rate: 'Tarifa', minimum: 'mínimo', years: 'años',
    filters: 'Filtros', sortby: 'Ordenar por', list: 'Lista', map: 'Mapa', week: 'Semana', month: 'Mes',
    nothing: 'Todavía no hay nada', anyday: 'Cualquier día', pickdate: 'Elegir fecha',
    cheapest: 'Tarifa más baja', dearest: 'Tarifa más alta', bestrated: 'Mejor valorados', bestmatch: 'Más compatible',
    soonest: 'Más pronto', mosthours: 'Más horas', nearby: 'Cerca',
    schedule: 'Horario', earnings: 'Ganancias', timeoff: 'Días libres', usualweek: 'Su semana habitual',
    allday: 'Todo el día', from: 'Desde', to: 'Hasta', reason: 'Motivo', note: 'Nota',
    applied_tab: 'Postulado',

    /* section headings inside screens */
    h_aboutyou: 'Sobre usted',
    h_account: 'Cuenta',
    h_addcatalog: 'Añadir del catálogo',
    h_addown: 'Añadir su propia tarea',
    h_agreedlist: 'Lista de tareas acordada',
    h_alreadysent: 'Ya enviado',
    h_anythingelse: 'Algo más',
    h_basics: 'Lo básico',
    h_beingfound: 'Que la encuentren',
    h_blocked: 'Horas bloqueadas',
    h_bookagain: '¿Los contrataría otra vez?',
    h_fittogether: 'Trabajos que encajan',
    h_howcleanersrate: 'Cómo la califican',
    h_howtheyrated: 'Cómo lo califican',
    h_howyouhear: 'Cómo le avisamos',
    h_howyouwork: 'Cómo trabaja',
    h_inyourwords: 'En sus palabras',
    h_jobsinhome: 'Tareas de esta casa',
    h_justthistime: 'Solo esta vez',
    h_langshere: 'Idiomas que se hablan aquí',
    h_language: 'Idioma',
    h_notshown: 'No se le muestran',
    h_opentoday: 'Disponible hoy cerca',
    h_otherdevice: 'Usar Ozeret en otro aparato',
    h_overall: 'En general',
    h_practical: 'Lo práctico',
    h_progress: 'Progreso',
    h_rateandhours: 'Tarifa y horas',
    h_reliability: 'Fiabilidad',
    h_reportcal: 'Reportar un problema de calendario',
    h_reviews: 'Reseñas',
    h_size: 'Tamaño',
    h_standing: 'Tareas habituales',
    h_tellus: 'Cuéntenos',
    h_thehome: 'La casa',
    h_thelist: 'La lista',
    h_thisvisit: 'Esta visita',
    h_whatabout: '¿De qué se trata?',
    h_whatcleanerssay: 'Lo que dicen los limpiadores',
    h_whatcleanerswrote: 'Lo que escribieron los limpiadores',
    h_whattheydid: 'Lo que hicieron',
    h_whattheyneed: 'Lo que necesitan',
    h_whatwasdone: 'Lo que se hizo',
    h_where: 'Dónde',
    h_wherereach: 'Cómo contactarle',
    h_whichjobs: 'Qué tareas esta vez',
    h_whichproperty: 'Cuál propiedad',
    h_who: 'Quién',
    h_whyshown: 'Por qué le mostramos esto',
    h_yourhome: 'Su casa',
    h_yourprops: 'Sus propiedades',
    h_yourrequest: 'Su solicitud',
    h_intheirwords: 'En sus palabras',
    h_howtheywork: 'Cómo trabaja',
    t_admin: 'Panel', t_sql: 'SQL',
  },

  ht: {
    tab_work: 'Travay', tab_calendar: 'Kalandriye', tab_myjobs: 'Travay mwen', tab_chats: 'Mesaj',
    tab_profile: 'Pwofil', tab_find: 'Chèche', tab_requests: 'Demann', tab_post: 'Poste',
    tab_myhome: 'Kay mwen',

    t_findwork: 'Chèche travay', t_thejob: 'Travay la', t_mycalendar: 'Kalandriye mwen',
    t_myjobs: 'Travay mwen', t_booking: 'Rezèvasyon', t_messages: 'Mesaj', t_myprofile: 'Pwofil mwen',
    t_findcleaner: 'Chèche moun pou netwaye', t_profile: 'Pwofil', t_whatneed: 'Kisa ou bezwen?',
    t_myrequests: 'Demann mwen', t_applicants: 'Kandida', t_myjoblist: 'Travay nan kay mwen',
    t_myhome: 'Kay mwen', t_updates: 'Nouvèl', t_review: 'Ekri yon avi',
    t_editprofile: 'Chanje pwofil', t_feedback: 'Voye kòmantè', t_editrequest: 'Chanje demann',
    t_pastjob: 'Anrejistre yon ansyen travay', t_settings: 'Paramèt', t_inprogress: 'Travay la',

    b_apply: 'Mwen disponib', b_offer: 'Ofri pri mwen', b_message: 'Mesaj', b_withdraw: 'Retire',
    b_save: 'Anrejistre', b_cancel: 'Anile', b_close: 'Fèmen', b_show: 'Montre rezilta', b_clear: 'Efase tout',
    b_edit: 'Chanje', b_remove: 'Retire', b_add: 'Ajoute', b_send: 'Voye', b_choose: 'Chwazi',
    b_post: 'Poste — moun ka wè li', b_block: 'Anrejistre jou sa yo', b_submit: 'Voye avi',
    b_finish: 'Make fini', b_again: 'Rezève ankò', b_signout: 'Dekonekte', b_saveprofile: 'Anrejistre pwofil',
    b_negotiate: 'Pwopoze yon pri', b_accept: 'Aksepte', b_decline: 'Refize', b_invite: 'Envite',

    perhour: '/è', hours: 'è', total: 'Total', when: 'Kilè', time: 'Lè', pay: 'Peman',
    onetime: 'Yon sèl fwa', everyweek: 'Chak semèn', applied: 'aplike', reviews: 'avi',
    match: 'konvni', available: 'Disponib', unavailable: 'Pa disponib', booked: 'Rezève',
    partly: 'Mwatye jou', today: 'Jodi a', tomorrow: 'Demen', thisweek: 'Semèn sa a',
    upcoming: 'K ap vini', finished: 'Fini', languages: 'Lang', specialties: 'Espesyalite',
    areas: 'Zòn', rate: 'Pri', minimum: 'minimòm', years: 'ane',
    filters: 'Filtè', sortby: 'Klase pa', list: 'Lis', map: 'Kat', week: 'Semèn', month: 'Mwa',
    nothing: 'Pa gen anyen ankò', anyday: 'Nenpòt jou', pickdate: 'Chwazi yon dat',
    cheapest: 'Pri pi ba', dearest: 'Pri pi wo', bestrated: 'Pi byen note', bestmatch: 'Pi bon match',
    soonest: 'Pi bonè', mosthours: 'Plis è', nearby: 'Toupre',
    schedule: 'Orè', earnings: 'Lajan', timeoff: 'Jou konje', usualweek: 'Semèn nòmal ou',
    allday: 'Tout jounen', from: 'Depi', to: 'Jiska', reason: 'Rezon', note: 'Nòt',
    applied_tab: 'Aplike',

    /* section headings inside screens */
    h_aboutyou: 'Sou ou',
    h_account: 'Kont',
    h_addcatalog: 'Ajoute nan katalòg la',
    h_addown: 'Ajoute pwòp travay ou',
    h_agreedlist: 'Lis travay yo dakò',
    h_alreadysent: 'Deja voye',
    h_anythingelse: 'Nenpòt lòt bagay',
    h_basics: 'Baz yo',
    h_beingfound: 'Pou yo jwenn ou',
    h_blocked: 'Èdtan bloke',
    h_bookagain: 'Èske w ta rele yo ankò',
    h_fittogether: 'Travay ki mache ansanm',
    h_howcleanersrate: 'Kijan netwayè yo evalye w',
    h_howtheyrated: 'Kijan yo evalye',
    h_howyouhear: 'Kijan n ap kontakte w',
    h_howyouwork: 'Kijan ou travay',
    h_inyourwords: 'Nan pwòp mo ou',
    h_jobsinhome: 'Travay nan kay sa a',
    h_justthistime: 'Sèlman fwa sa a',
    h_langshere: 'Lang yo pale isit la',
    h_language: 'Lang',
    h_notshown: 'Yo pa parèt pou ou',
    h_opentoday: 'Ouvè jodi a toupre w',
    h_otherdevice: 'Sèvi ak Ozeret sou yon lòt aparèy',
    h_overall: 'An jeneral',
    h_practical: 'Bagay pratik',
    h_progress: 'Pwogrè',
    h_rateandhours: 'Tarif ak èdtan',
    h_reliability: 'Serye',
    h_reportcal: 'Siyale yon pwoblèm kalandriye',
    h_reviews: 'Kòmantè',
    h_size: 'Gwosè',
    h_standing: 'Travay regilye',
    h_tellus: 'Di nou',
    h_thehome: 'Kay la',
    h_thelist: 'Lis la',
    h_thisvisit: 'Vizit sa a',
    h_whatabout: 'Sou kisa li ye',
    h_whatcleanerssay: 'Sa netwayè yo di sou yo',
    h_whatcleanerswrote: 'Sa netwayè yo ekri',
    h_whattheydid: 'Sa yo te fè',
    h_whattheyneed: 'Sa yo bezwen fè',
    h_whatwasdone: 'Sa ki te fèt',
    h_where: 'Ki kote',
    h_wherereach: 'Kote pou n jwenn ou',
    h_whichjobs: 'Ki travay fwa sa a',
    h_whichproperty: 'Ki pwopriyete',
    h_who: 'Kilès',
    h_whyshown: 'Poukisa nou montre w sa',
    h_yourhome: 'Kay ou',
    h_yourprops: 'Pwopriyete ou yo',
    h_yourrequest: 'Demann ou',
    h_intheirwords: 'Nan pwòp mo li',
    h_howtheywork: 'Kijan li travay',
    t_admin: 'Tablo',
  },

  yi: {
    tab_work: 'אַרבעט', tab_calendar: 'קאַלענדאַר', tab_myjobs: 'מײַנע אַרבעטן',
    tab_chats: 'נאָכריכטן', tab_profile: 'פּראָפֿיל', tab_find: 'זוכן',
    tab_requests: 'אָנפֿראגעס', tab_post: 'אַרויסשטעלן', tab_myhome: 'מײַן הויז',

    t_findwork: 'זוכן אַרבעט', t_thejob: 'די אַרבעט', t_mycalendar: 'מײַן קאַלענדאַר',
    t_myjobs: 'מײַנע אַרבעטן', t_booking: 'באַשטעלונג', t_messages: 'נאָכריכטן',
    t_myprofile: 'מײַן פּראָפֿיל', t_findcleaner: 'זוכן אַ רייניקער', t_profile: 'פּראָפֿיל',
    t_whatneed: 'וואָס דאַרפֿט איר?', t_myrequests: 'מײַנע אָנפֿראגעס', t_applicants: 'קאַנדידאַטן',
    t_myjoblist: 'אַרבעטן אין מײַן הויז', t_myhome: 'מײַן הויז', t_updates: 'נײַעס',
    t_review: 'שרײַבט אַ באַװערטונג', t_editprofile: 'ענדערן פּראָפֿיל',
    t_feedback: 'שיקט באַמערקונגען', t_editrequest: 'ענדערן אָנפֿראַגע',
    t_pastjob: 'פֿאַרשרײַבט אַ פֿריִערדיקע אַרבעט', t_settings: 'אײַנשטעלונגען', t_inprogress: 'די אַרבעט',

    b_apply: 'איך בין פֿרײַ', b_offer: 'גיט מײַן פּרײַז', b_message: 'נאָכריכט',
    b_withdraw: 'צוריקציִען', b_save: 'אָפּהיטן', b_cancel: 'אָפּזאָגן', b_close: 'פֿאַרמאַכן',
    b_show: 'װײַזן רעזולטאַטן', b_clear: 'אױסמעקן אַלץ', b_edit: 'ענדערן', b_remove: 'אַראָפּנעמען',
    b_add: 'צוגעבן', b_send: 'שיקן', b_choose: 'אױסקלײַבן', b_post: 'אַרויסשטעלן',
    b_block: 'אָפּהיטן די טעג', b_submit: 'שיקט די באַװערטונג', b_finish: 'פֿאַרטיק',
    b_again: 'נאָך אַ מאָל באַשטעלן', b_signout: 'אַרויסגיין', b_saveprofile: 'אָפּהיטן פּראָפֿיל',
    b_negotiate: 'פֿאָרלייגן אַ פּרײַז', b_accept: 'אָננעמען', b_decline: 'אָפּזאָגן',
    b_invite: 'פֿאַרבעטן',

    perhour: '/שעה', hours: 'שעה', total: 'סך הכל', when: 'װען', time: 'צײַט', pay: 'צאָלונג',
    onetime: 'אײן מאָל', everyweek: 'יעדע װאָך', applied: 'געבעטן', reviews: 'באַװערטונגען',
    match: 'פּאַסיק', available: 'פֿרײַ', unavailable: 'נישט פֿרײַ', booked: 'באַשטעלט',
    partly: 'האַלבער טאָג', today: 'הײַנט', tomorrow: 'מאָרגן', thisweek: 'די װאָך',
    upcoming: 'קומענדיקע', finished: 'פֿאַרטיקע', languages: 'שפּראַכן',
    specialties: 'ספּעציאַליטעטן', areas: 'געגנטן', rate: 'פּרײַז', minimum: 'מינימום',
    years: 'יאָר', filters: 'פֿילטערס', sortby: 'סאָרטירן', list: 'רשימה', map: 'מאַפּע',
    week: 'װאָך', month: 'חודש', nothing: 'נאָך גאָרנישט', anyday: 'װאָס פֿאַר אַ טאָג',
    pickdate: 'קלײַבט אַ דאַטע', cheapest: 'ביליקסטער', dearest: 'טײַערסטער',
    bestrated: 'בעסטע באַװערטונג', bestmatch: 'בעסטע פּאַסיק', soonest: 'פֿריִער',
    mosthours: 'מער שעה', nearby: 'נאָענט', schedule: 'צײַטפּלאַן', earnings: 'פֿאַרדינסט',
    timeoff: 'פֿרײַע טעג', usualweek: 'אײַער געװײנטלעכע װאָך', allday: 'אַ גאַנצן טאָג',
    from: 'פֿון', to: 'ביז', reason: 'אורזאַך', note: 'נאָטיץ', applied_tab: 'געבעטן',

    /* section headings inside screens */
    h_aboutyou: 'וועגן אײַך',
    h_account: 'קאנטע',
    h_addcatalog: 'צוגעבן פֿון קאַטאַלאָג',
    h_addown: 'צוגעבן אַן אייגענע אַרבעט',
    h_agreedlist: 'אָפּגערעדטע רשימה',
    h_alreadysent: 'שוין געשיקט',
    h_anythingelse: 'נאָך עפּעס',
    h_basics: 'גרונט',
    h_beingfound: 'געפֿונען ווערן',
    h_blocked: 'פֿאַרנומענע שעהען',
    h_bookagain: 'וואָלט איר זיי ווידער גענומען',
    h_fittogether: 'אַרבעטן וואָס פּאַסן צוזאַמען',
    h_howcleanersrate: 'ווי מען שאַצט אײַך',
    h_howtheyrated: 'ווי מען שאַצט זיי',
    h_howyouhear: 'ווי מיר לאָזן אײַך וויסן',
    h_howyouwork: 'ווי איר אַרבעט',
    h_inyourwords: 'אין אײַערע ווערטער',
    h_jobsinhome: 'אַרבעטן אין דעם הויז',
    h_justthistime: 'נאָר דאָס מאָל',
    h_langshere: 'שפּראַכן וואָס מען רעדט דאָ',
    h_language: 'שפּראַך',
    h_notshown: 'ניט געוויזן פֿאַר אײַך',
    h_opentoday: 'הײַנט אָפֿן לעבן אײַך',
    h_otherdevice: 'ניצן Ozeret אויף אַן אַנדער אַפּאַראַט',
    h_overall: 'בכלל',
    h_practical: 'פּראַקטיש',
    h_progress: 'פֿאָרשריט',
    h_rateandhours: 'טאַריף און שעהען',
    h_reliability: 'פֿאַרלאָזלעכקייט',
    h_reportcal: 'מעלדן אַ קאַלענדאַר פּראָבלעם',
    h_reviews: 'רעצענזיעס',
    h_size: 'גרייס',
    h_standing: 'שטענדיקע אַרבעטן',
    h_tellus: 'זאָגט אונדז',
    h_thehome: 'דאָס הויז',
    h_thelist: 'די רשימה',
    h_thisvisit: 'דער דאָזיקער וויזיט',
    h_whatabout: 'וועגן וואָס',
    h_whatcleanerssay: 'וואָס רייניקער זאָגן וועגן זיי',
    h_whatcleanerswrote: 'וואָס רייניקער האָבן געשריבן',
    h_whattheydid: 'וואָס זיי האָבן געטאָן',
    h_whattheyneed: 'וואָס זיי דאַרפֿן',
    h_whatwasdone: 'וואָס איז געטאָן געוואָרן',
    h_where: 'וווּ',
    h_wherereach: 'ווי מיר גרייכן אײַך',
    h_whichjobs: 'וועלכע אַרבעטן דאָס מאָל',
    h_whichproperty: 'וועלכע דירה',
    h_who: 'ווער',
    h_whyshown: 'פֿאַר וואָס מיר האָבן דאָס געוויזן',
    h_yourhome: 'אײַער הויז',
    h_yourprops: 'אײַערע דירות',
    h_yourrequest: 'אײַער בקשה',
    h_intheirwords: 'אין זייערע ווערטער',
    h_howtheywork: 'ווי זיי אַרבעטן',
    t_admin: 'טאַבלאָ',
  },

  he: {
    tab_work: 'עבודה', tab_calendar: 'לוח שנה', tab_myjobs: 'העבודות שלי',
    tab_chats: 'הודעות', tab_profile: 'פרופיל', tab_find: 'חיפוש',
    tab_requests: 'בקשות', tab_post: 'פרסום', tab_myhome: 'הבית שלי',

    t_findwork: 'חיפוש עבודה', t_thejob: 'העבודה', t_mycalendar: 'לוח השנה שלי',
    t_myjobs: 'העבודות שלי', t_booking: 'הזמנה', t_messages: 'הודעות',
    t_myprofile: 'הפרופיל שלי', t_findcleaner: 'חיפוש מנקה', t_profile: 'פרופיל',
    t_whatneed: 'מה צריך?', t_myrequests: 'הבקשות שלי', t_applicants: 'מועמדים',
    t_myjoblist: 'עבודות בבית שלי', t_myhome: 'הבית שלי', t_updates: 'עדכונים',
    t_review: 'כתיבת חוות דעת', t_editprofile: 'עריכת פרופיל',
    t_feedback: 'שליחת משוב', t_editrequest: 'עריכת בקשה',
    t_pastjob: 'רישום עבודה קודמת', t_settings: 'הגדרות', t_inprogress: 'העבודה',

    b_apply: 'אני פנויה', b_offer: 'הצעת המחיר שלי', b_message: 'הודעה',
    b_withdraw: 'ביטול מועמדות', b_save: 'שמירה', b_cancel: 'ביטול', b_close: 'סגירה',
    b_show: 'הצגת תוצאות', b_clear: 'ניקוי הכל', b_edit: 'עריכה', b_remove: 'הסרה',
    b_add: 'הוספה', b_send: 'שליחה', b_choose: 'בחירה', b_post: 'פרסום',
    b_block: 'שמירת הימים', b_submit: 'שליחת חוות דעת', b_finish: 'סימון כהושלם',
    b_again: 'הזמנה נוספת', b_signout: 'התנתקות', b_saveprofile: 'שמירת פרופיל',
    b_negotiate: 'הצעת מחיר', b_accept: 'אישור', b_decline: 'דחייה', b_invite: 'הזמנה',

    perhour: '/שעה', hours: 'שעות', total: 'סך הכל', when: 'מתי', time: 'שעה',
    pay: 'תשלום', onetime: 'חד פעמי', everyweek: 'כל שבוע', applied: 'הגישו מועמדות',
    reviews: 'חוות דעת', match: 'התאמה', available: 'פנויה', unavailable: 'לא פנויה',
    booked: 'מוזמן', partly: 'חצי יום', today: 'היום', tomorrow: 'מחר', thisweek: 'השבוע',
    upcoming: 'הקרובות', finished: 'הושלמו', languages: 'שפות', specialties: 'התמחויות',
    areas: 'אזורים', rate: 'תעריף', minimum: 'מינימום', years: 'שנים',
    filters: 'סינון', sortby: 'מיון', list: 'רשימה', map: 'מפה', week: 'שבוע',
    month: 'חודש', nothing: 'עדיין אין כלום', anyday: 'כל יום', pickdate: 'בחירת תאריך',
    cheapest: 'הזול ביותר', dearest: 'היקר ביותר', bestrated: 'הדירוג הגבוה',
    bestmatch: 'ההתאמה הטובה', soonest: 'הקרוב ביותר', mosthours: 'הכי הרבה שעות',
    nearby: 'קרוב', schedule: 'לוח זמנים', earnings: 'הכנסות', timeoff: 'ימי חופש',
    usualweek: 'השבוע הרגיל שלך', allday: 'כל היום', from: 'מ', to: 'עד',
    reason: 'סיבה', note: 'הערה', applied_tab: 'מועמדויות',

    /* section headings inside screens */
    h_aboutyou: 'עליך',
    h_account: 'חשבון',
    h_addcatalog: 'הוספה מהקטלוג',
    h_addown: 'הוספת עבודה משלך',
    h_agreedlist: 'רשימת העבודות שסוכמה',
    h_alreadysent: 'כבר נשלח',
    h_anythingelse: 'משהו נוסף',
    h_basics: 'בסיסי',
    h_beingfound: 'להימצא',
    h_blocked: 'שעות חסומות',
    h_bookagain: 'האם תזמין שוב',
    h_fittogether: 'עבודות שמסתדרות יחד',
    h_howcleanersrate: 'איך המנקים מדרגים אותך',
    h_howtheyrated: 'איך הם מדורגים',
    h_howyouhear: 'איך נעדכן אותך',
    h_howyouwork: 'איך אתה עובד',
    h_inyourwords: 'במילים שלך',
    h_jobsinhome: 'עבודות בבית הזה',
    h_justthistime: 'רק הפעם',
    h_langshere: 'שפות המדוברות כאן',
    h_language: 'שפה',
    h_notshown: 'לא מוצג לך',
    h_opentoday: 'פתוח היום בקרבתך',
    h_otherdevice: 'שימוש ב-Ozeret במכשיר אחר',
    h_overall: 'כללי',
    h_practical: 'מעשי',
    h_progress: 'התקדמות',
    h_rateandhours: 'תעריף ושעות',
    h_reliability: 'אמינות',
    h_reportcal: 'דיווח על בעיה ביומן',
    h_reviews: 'ביקורות',
    h_size: 'גודל',
    h_standing: 'עבודות קבועות',
    h_tellus: 'ספרו לנו',
    h_thehome: 'הבית',
    h_thelist: 'הרשימה',
    h_thisvisit: 'הביקור הזה',
    h_whatabout: 'על מה מדובר',
    h_whatcleanerssay: 'מה המנקים אומרים עליהם',
    h_whatcleanerswrote: 'מה המנקים כתבו',
    h_whattheydid: 'מה הם עשו',
    h_whattheyneed: 'מה הם צריכים',
    h_whatwasdone: 'מה נעשה',
    h_where: 'היכן',
    h_wherereach: 'איך נשיג אותך',
    h_whichjobs: 'אילו עבודות הפעם',
    h_whichproperty: 'איזה נכס',
    h_who: 'מי',
    h_whyshown: 'למה הראינו לך את זה',
    h_yourhome: 'הבית שלך',
    h_yourprops: 'הנכסים שלך',
    h_yourrequest: 'הבקשה שלך',
    h_intheirwords: 'במילים שלהם',
    h_howtheywork: 'איך הם עובדים',
    t_admin: 'לוח בקרה',
  },

  ru: {
    tab_work: 'Работа', tab_calendar: 'Календарь', tab_myjobs: 'Мои работы', tab_chats: 'Чаты',
    tab_profile: 'Профиль', tab_find: 'Поиск', tab_requests: 'Заявки', tab_post: 'Разместить',
    tab_myhome: 'Мой дом',

    t_findwork: 'Найти работу', t_thejob: 'Работа', t_mycalendar: 'Мой календарь',
    t_myjobs: 'Мои работы', t_booking: 'Бронь', t_messages: 'Сообщения', t_myprofile: 'Мой профиль',
    t_findcleaner: 'Найти уборщицу', t_profile: 'Профиль', t_whatneed: 'Что вам нужно?',
    t_myrequests: 'Мои заявки', t_applicants: 'Кандидаты', t_myjoblist: 'Работы по дому',
    t_myhome: 'Мой дом', t_updates: 'Обновления', t_review: 'Написать отзыв',
    t_editprofile: 'Изменить профиль', t_feedback: 'Отправить отзыв', t_editrequest: 'Изменить заявку',
    t_pastjob: 'Добавить прошлую работу', t_settings: 'Настройки', t_inprogress: 'Работа',

    b_apply: 'Я свободна', b_offer: 'Предложить свою ставку', b_message: 'Сообщение', b_withdraw: 'Отозвать',
    b_save: 'Сохранить', b_cancel: 'Отмена', b_close: 'Закрыть', b_show: 'Показать', b_clear: 'Очистить',
    b_edit: 'Изменить', b_remove: 'Убрать', b_add: 'Добавить', b_send: 'Отправить', b_choose: 'Выбрать',
    b_post: 'Разместить — видно уборщицам', b_block: 'Сохранить эти дни', b_submit: 'Отправить отзыв',
    b_finish: 'Отметить выполненным', b_again: 'Забронировать снова', b_signout: 'Выйти', b_saveprofile: 'Сохранить профиль',
    b_negotiate: 'Предложить цену', b_accept: 'Принять', b_decline: 'Отклонить', b_invite: 'Пригласить',

    perhour: '/час', hours: 'часов', total: 'Всего', when: 'Когда', time: 'Время', pay: 'Оплата',
    onetime: 'Один раз', everyweek: 'Каждую неделю', applied: 'откликнулись', reviews: 'отзывов',
    match: 'совпадение', available: 'Свободна', unavailable: 'Занята', booked: 'Забронировано',
    partly: 'Полдня', today: 'Сегодня', tomorrow: 'Завтра', thisweek: 'На этой неделе',
    upcoming: 'Предстоящие', finished: 'Завершённые', languages: 'Языки', specialties: 'Специализация',
    areas: 'Районы', rate: 'Ставка', minimum: 'минимум', years: 'лет',
    filters: 'Фильтры', sortby: 'Сортировка', list: 'Список', map: 'Карта', week: 'Неделя', month: 'Месяц',
    nothing: 'Пока пусто', anyday: 'Любой день', pickdate: 'Выбрать дату',
    cheapest: 'Дешевле', dearest: 'Дороже', bestrated: 'Лучший рейтинг', bestmatch: 'Лучшее совпадение',
    soonest: 'Скорее всего', mosthours: 'Больше часов', nearby: 'Рядом',
    schedule: 'Расписание', earnings: 'Заработок', timeoff: 'Выходные', usualweek: 'Обычная неделя',
    allday: 'Весь день', from: 'С', to: 'До', reason: 'Причина', note: 'Заметка',
    applied_tab: 'Отклики',

    /* section headings inside screens */
    h_aboutyou: 'О вас',
    h_account: 'Аккаунт',
    h_addcatalog: 'Добавить из каталога',
    h_addown: 'Добавить свою задачу',
    h_agreedlist: 'Согласованный список',
    h_alreadysent: 'Уже отправлено',
    h_anythingelse: 'Что-нибудь ещё',
    h_basics: 'Основное',
    h_beingfound: 'Чтобы вас находили',
    h_blocked: 'Занятые часы',
    h_bookagain: 'Наняли бы снова?',
    h_fittogether: 'Работы, что складываются вместе',
    h_howcleanersrate: 'Как вас оценивают',
    h_howtheyrated: 'Как их оценивают',
    h_howyouhear: 'Как мы вас уведомляем',
    h_howyouwork: 'Как вы работаете',
    h_inyourwords: 'Вашими словами',
    h_jobsinhome: 'Задачи в этом доме',
    h_justthistime: 'Только на этот раз',
    h_langshere: 'Языки в этом доме',
    h_language: 'Язык',
    h_notshown: 'Скрыто от вас',
    h_opentoday: 'Открыто сегодня рядом',
    h_otherdevice: 'Ozeret на другом устройстве',
    h_overall: 'В целом',
    h_practical: 'Практическое',
    h_progress: 'Ход работы',
    h_rateandhours: 'Ставка и часы',
    h_reliability: 'Надёжность',
    h_reportcal: 'Сообщить о проблеме с календарём',
    h_reviews: 'Отзывы',
    h_size: 'Размер',
    h_standing: 'Постоянные задачи',
    h_tellus: 'Расскажите',
    h_thehome: 'Дом',
    h_thelist: 'Список',
    h_thisvisit: 'Этот визит',
    h_whatabout: 'О чём это',
    h_whatcleanerssay: 'Что говорят уборщики',
    h_whatcleanerswrote: 'Что написали уборщики',
    h_whattheydid: 'Что было сделано',
    h_whattheyneed: 'Что нужно сделать',
    h_whatwasdone: 'Что сделано',
    h_where: 'Где',
    h_wherereach: 'Как с вами связаться',
    h_whichjobs: 'Какие задачи в этот раз',
    h_whichproperty: 'Какой объект',
    h_who: 'Кто',
    h_whyshown: 'Почему мы это показали',
    h_yourhome: 'Ваш дом',
    h_yourprops: 'Ваши объекты',
    h_yourrequest: 'Ваш запрос',
    h_intheirwords: 'Их словами',
    h_howtheywork: 'Как они работают',
    t_admin: 'Панель',
  },
};

/** Job catalog labels. English lives on the job object itself. */
export const JOB_I18N: Record<string, Dict> = {
  yi: {
    dishes: 'כלים און אָפּגאָס', counters: 'טישן', stovetop: 'קאָכאויוון',
    oven: 'אינעווייניק אין אויוון', fridge: 'אינעווייניק אין אײַזקאַסטן',
    microwave: 'מיקראָװעל', cabinets: 'שאַפֿע־טירן',
    kosher: 'כּשרע קיך (פֿליישיק / מילכיק)', pesach: 'פּסח רייניקונג',
    toilets: 'קלאָזעטן', showers: 'שאַװערס און װאַנעס', bath_sinks: 'אָפּגאָסן און שפּיגלען',
    grout: 'קאַפֿלען און פֿוגן', restock: 'האַנטעך און פּאַפּיר',
    vacuum: 'קערן און שטויבזויגן', mop: 'װאַשן די פּאָדלאָגע', stairs: 'טרעפּ',
    carpet_spot: 'פֿלעקן אױפֿן טעפּעך', linens: 'בעטגעװאַנט בײַטן', beds: 'בעטן מאַכן',
    dusting: 'שטויב אָפּװישן', tidy: 'אױפֿרױמען', windows_in: 'פֿענצטער (אינעװײניק)',
    blinds: 'זשאַלוזיעס', baseboards: 'לײַסטן', closets: 'שאַפֿעס אָרדענען',
    laundry_wash: 'װאַשן און טריקענען', laundry_fold: 'צונויפֿלייגן און אַװעקלייגן',
    ironing: 'פּרעסן', delicates: 'האַנט־װאַשן', shabbos: 'צוגרייטן אויף שבת',
    yomtov: 'צוגרייטן אויף יום טוב', post_party: 'נאָך אַ שׂימחה', deep_clean: 'טיפֿע רייניקונג',
    move_out: 'אַרײַנציִען / אַרויסציִען', post_reno: 'נאָך בױען', pets: 'בעלי־חיים ערטער',
    basement: 'קעלער / שפּילצימער', garage: 'גאַראַזש אָדער גאַניק',
    meal_prep: 'גרינגע עסן צוגרייטן', groceries: 'שפּײַז אַװעקלייגן', kids_tidy: 'קינדער צימערן',
  },
  he: {
    dishes: 'כלים וכיור', counters: 'משטחים', stovetop: 'כיריים',
    oven: 'תנור מבפנים', fridge: 'מקרר מבפנים', microwave: 'מיקרוגל',
    cabinets: 'חזיתות ארונות', kosher: 'מטבח כשר (בשרי / חלבי)', pesach: 'ניקיון לפסח',
    toilets: 'שירותים', showers: 'מקלחות ואמבטיות', bath_sinks: 'כיורים ומראות',
    grout: 'אריחים ורובה', restock: 'מגבות ונייר', vacuum: 'טאטוא ושאיבה',
    mop: 'שטיפת רצפות', stairs: 'מדרגות', carpet_spot: 'כתמים בשטיח',
    linens: 'החלפת מצעים', beds: 'סידור מיטות', dusting: 'ניגוב אבק',
    tidy: 'סידור', windows_in: 'חלונות (מבפנים)', blinds: 'תריסים',
    baseboards: 'פנלים', closets: 'סידור ארונות', laundry_wash: 'כביסה וייבוש',
    laundry_fold: 'קיפול וסידור', ironing: 'גיהוץ', delicates: 'כביסה עדינה',
    shabbos: 'הכנות לשבת', yomtov: 'הכנות לחג', post_party: 'אחרי אירוע',
    deep_clean: 'ניקיון יסודי', move_out: 'כניסה / יציאה מדירה',
    post_reno: 'אחרי שיפוץ', pets: 'אזורי חיות מחמד', basement: 'מרתף / חדר משחקים',
    garage: 'חניה או מרפסת', meal_prep: 'הכנת אוכל קל', groceries: 'סידור קניות',
    kids_tidy: 'חדרי ילדים',
  },

  es: {
    dishes: 'Platos y fregadero', counters: 'Encimeras', stovetop: 'Estufa y campana',
    oven: 'Interior del horno', fridge: 'Interior del refrigerador', microwave: 'Microondas',
    cabinets: 'Frentes de gabinetes', kosher: 'Cocina kosher (carne / lácteos)', pesach: 'Limpieza de Pésaj',
    toilets: 'Inodoros', showers: 'Duchas y bañeras', bath_sinks: 'Lavabos y espejos',
    grout: 'Azulejos y juntas', restock: 'Reponer toallas y papel',
    vacuum: 'Barrer y aspirar', mop: 'Trapear pisos', stairs: 'Escaleras', carpet_spot: 'Manchas de alfombra',
    linens: 'Cambiar sábanas', beds: 'Hacer camas', dusting: 'Quitar el polvo', tidy: 'Ordenar',
    windows_in: 'Ventanas (por dentro)', blinds: 'Persianas', baseboards: 'Zócalos', closets: 'Organizar clósets',
    laundry_wash: 'Lavar y secar', laundry_fold: 'Doblar y guardar', ironing: 'Planchar', delicates: 'Lavar a mano',
    shabbos: 'Preparación de Shabat', yomtov: 'Preparación de fiesta', post_party: 'Después de una fiesta',
    deep_clean: 'Limpieza profunda', move_out: 'Mudanza', post_reno: 'Después de obra',
    pets: 'Zona de mascotas', basement: 'Sótano / sala de juegos', garage: 'Garaje o porche',
    meal_prep: 'Preparar comida ligera', groceries: 'Guardar la compra', kids_tidy: 'Cuartos de niños',
  },
  ht: {
    dishes: 'Vesèl ak lavabo', counters: 'Kontwa', stovetop: 'Recho', oven: 'Anndan fou a',
    fridge: 'Anndan frijidè', microwave: 'Mikwo-ond', cabinets: 'Devan amwa',
    kosher: 'Kwizin kosher (vyann / lèt)', pesach: 'Netwayaj Pesach',
    toilets: 'Twalèt', showers: 'Douch ak beny', bath_sinks: 'Lavabo ak miwa',
    grout: 'Mozayik ak jwen', restock: 'Ranplase sèvyèt ak papye',
    vacuum: 'Bale ak aspiratè', mop: 'Pase mòp', stairs: 'Eskalye', carpet_spot: 'Tach sou tapi',
    linens: 'Chanje dra', beds: 'Fè kabann', dusting: 'Retire pousyè', tidy: 'Ranje',
    windows_in: 'Fenèt (anndan)', blinds: 'Rido di', baseboards: 'Plent', closets: 'Ranje amwa',
    laundry_wash: 'Lave ak seche', laundry_fold: 'Plise ak ranje', ironing: 'Pase rad', delicates: 'Lave nan men',
    shabbos: 'Preparasyon Shabbat', yomtov: 'Preparasyon fèt', post_party: 'Apre yon fèt',
    deep_clean: 'Gwo netwayaj', move_out: 'Demenajman', post_reno: 'Apre konstriksyon',
    pets: 'Kote bèt yo', basement: 'Sousòl / sal jwèt', garage: 'Garaj oswa galri',
    meal_prep: 'Prepare yon ti manje', groceries: 'Ranje pwovizyon', kids_tidy: 'Chanm timoun',
  },

  ru: {
    dishes: 'Посуда и раковина', counters: 'Столешницы', stovetop: 'Плита и вытяжка',
    oven: 'Духовка внутри', fridge: 'Холодильник внутри', microwave: 'Микроволновка',
    cabinets: 'Фасады шкафов', kosher: 'Кошерная кухня (мясное / молочное)', pesach: 'Уборка к Песаху',
    toilets: 'Туалеты', showers: 'Душ и ванна', bath_sinks: 'Раковины и зеркала',
    grout: 'Плитка и швы', restock: 'Полотенца и бумага',
    vacuum: 'Подмести и пропылесосить', mop: 'Помыть полы', stairs: 'Лестницы', carpet_spot: 'Пятна на ковре',
    linens: 'Сменить бельё', beds: 'Заправить кровати', dusting: 'Протереть пыль', tidy: 'Убрать вещи',
    windows_in: 'Окна (изнутри)', blinds: 'Жалюзи', baseboards: 'Плинтусы', closets: 'Разобрать шкафы',
    laundry_wash: 'Стирка и сушка', laundry_fold: 'Сложить и убрать', ironing: 'Глажка', delicates: 'Ручная стирка',
    shabbos: 'Подготовка к шаббату', yomtov: 'Подготовка к празднику', post_party: 'После праздника',
    deep_clean: 'Генеральная уборка', move_out: 'Переезд', post_reno: 'После ремонта',
    pets: 'Места животных', basement: 'Подвал / игровая', garage: 'Гараж или крыльцо',
    meal_prep: 'Простая готовка', groceries: 'Разобрать продукты', kids_tidy: 'Детские комнаты',
  },
};

let current = 'en';

export function getLang(): string {
  return current;
}

export function setLang(id: string): void {
  current = id;
  try {
    localStorage.setItem('ozeret_lang', id);
  } catch {
    /* private mode, or opened from a file:// URL */
  }
}

export function loadLang(): string {
  try {
    current = localStorage.getItem('ozeret_lang') || 'en';
  } catch {
    current = 'en';
  }
  return current;
}

/** A key with blanks filled in: tf('tx_beds', { n: 3 }) -> "3 bed". */
export function tf(key: string, vars: Record<string, string | number>): string {
  return t(key).replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
}

/** Translate a UI key. Unknown keys return themselves, which makes a missing
 *  translation obvious in the interface rather than silently blank. */
export function t(key: string): string {
  const dict = STRINGS[current];
  return (dict && dict[key]) || STRINGS.en[key] || key;
}

/* The cleaner screens, and what they share with homes, had English written
   straight into them; with Spanish chosen a cleaner still read "You applied",
   "Thu 22 Oct" and "3 homes looking right now". These are those strings.
   Other languages fall back to English, as everywhere else. */
Object.assign(STRINGS.en, {
  tx_refreshing: 'Refreshing…',
  tx_release: 'Release to refresh',
  tx_pull: 'Pull to refresh',
  tx_promo_hint: 'Given a code? Type it here. A launch code from Ozeret adds free months to your account, on top of whatever you already have. A friend’s code earns them a free month rather than you.',
  tx_share_hint_1: 'Your own code. Anybody who signs up with it earns you a free month — they do not have to pay anything, they just have to join.',
  tx_share_hint_n: 'Your own code. Anybody who signs up with it earns you {n} free months — they do not have to pay anything, they just have to join.',
  tx_small_print: 'The small print',
  tx_small_print_hint: 'What Ozeret is and is not, and exactly what it holds about you.',
  tx_lang_hint: 'Changes the whole app straight away. Your profile text stays in whatever language you wrote it.',
  tx_never_ssn: 'Ozeret never asks for a social security number, immigration status, or any government document.',
  tx_dim_thorough: 'Thoroughness',
  tx_dim_reliable: 'On time & showed up',
  tx_dim_comm: 'Communication',
  tx_dim_care: 'Care with belongings',
  tx_dim_friendly: 'Friendly & respectful',
  tx_dim_clear: 'Clear about the work',
  tx_dim_paid: 'Paid as agreed',
  tx_dim_ready: 'Home was ready to clean',
  tx_homes_now_1: '1 home looking right now.',
  tx_homes_now_n: '{n} homes looking right now.',
  tx_homes_on_1: '1 home looking on {date}.',
  tx_homes_on_n: '{n} homes looking on {date}.',
  tx_homes_only_looking: 'Homes appear here only while they are actively looking.',
  tx_pair_lede: 'A second job the same day, close enough to reach in time. This is the difference between a half day and a full one.',
  tx_open: 'Open',
  tx_pair_travel: 'about {travel} · you have {gap} between them',
  tx_n_of_m: '{n} of {m}',
  tx_beds: '{n} bed',
  tx_baths: '{n} bath',
  tx_sqft: '{n} sq ft',
  tx_you_countered: 'You countered {rate}',
  tx_they_invited: 'They invited you',
  tx_you_applied: 'You applied',
  tx_reach_1: '1 job you can reach.',
  tx_reach_n: '{n} jobs you can reach.',
  tx_map_help: 'Tap a price to open that job. Where several share a postal code they share a tag — tap it to see them.',
  tx_unplaced: '{n} more have not given a postal code yet.',
  tx_job_gone: 'This job is no longer open',
  tx_job_gone_sub: 'The home has booked somebody or stopped looking.',
  tx_find_other: 'Find other work',
  tx_got_job: 'You got the job. Here is your booking.',
  tx_rated_them: '{n} cleaners rated them',
  tx_every_ongoing: 'Every {day}, ongoing. Agreeing holds the slot on your calendar until either side ends it.',
  tx_est_off: 'For this list in a home this size we would estimate around {s} hours. They asked for {h}. Worth agreeing what to prioritise before you start.',
  tx_est_ok: 'Our rough estimate for this list is about {s} hours — close to the {h} they asked for.',
  tx_supplies_yes: 'Supplies provided',
  tx_supplies_no: 'Bring your own supplies',
  tx_no_pets: 'No pets',
  tx_kids: '{n} kids',
  tx_no_kids: 'No kids',
  tx_elevator: 'Elevator',
  tx_stairs: 'Stairs',
  tx_ground: 'Ground floor',
  tx_x_invited_you: '{name} invited you',
  tx_accept_lede: 'Accept to tell them you can come. They confirm the booking.',
  tx_accept: 'Accept',
  tx_decline: 'Decline',
  tx_you_offered: 'You offered {rate}',
  tx_jobs_1: '1 job',
  tx_jobs_n: '{n} jobs',
  tx_nothing_booked: 'Nothing booked',
  tx_still_avail: 'Still available:',
  tx_nothing_more_today: 'nothing more today',
  tx_until_next: '{d} until the next one',
  tx_week_of: 'Week of {d}',
  tx_pct_booked: '{n}% booked',
  tx_still_open: 'Still open {n}h',
  tx_unsold: '{n} hours unsold this week.',
  tx_unsold_more: 'Homes filter by date — the more accurate your calendar, the more of these fill themselves.',
  tx_tapdays_more: 'Select as many as you like, then set them available, unavailable, or block certain hours. Confirmed jobs block themselves.',
  tx_earlier_months: 'Earlier months',
  tx_later_months: 'Later months',
  tx_this_month: 'This month',
  tx_blocked: 'Blocked',
  tx_not_working: 'Not working',
  tx_off: 'Off',
  tx_every_other: 'Every other {day}',
  tx_since: 'Since {d}',
  tx_hidden: 'You are hidden. Homes cannot find you.',
  tx_findable_cal: 'Homes find you on the days your calendar says you are free.',
  tx_cal_confirmed: 'Confirmed {d}',
  tx_default_week: 'This is your default. Anything you change on a specific day in the calendar wins over it.',
  tx_n_of_m_done: '{n} of {m} done',
  tx_finish_without: 'You can finish without ticking everything — say what happened on the ones you could not get to, and the home will see it.',
  tx_mark_not_done: 'Mark not done',
  tx_mark_done: 'Mark done',
  tx_edit_said: 'Edit what you said',
  tx_say_something: 'Say something about this job',
  tx_paid: 'Paid',
  tx_not_paid: 'Not paid',
  tx_confirmed: 'Confirmed',
  tx_cancelled: 'Cancelled',
  tx_not_marked_paid: 'Not marked paid yet',
  tx_you_not_marked_paid: 'You have not marked paid',
  tx_waiting_back: 'Waiting to hear back',
  tx_others_applied_1: '1 other applied',
  tx_others_applied_n: '{n} others applied',
  tx_only_you: 'Only you so far',
  tx_address: 'Address',
  tx_you: 'You',
  tx_marked_paid: '{who} marked this paid on {d}',
  tx_tick_paid_home: 'Tick this once you have paid, so you can both see what is outstanding.',
  tx_tick_paid_cleaner: 'Tick this once you have been paid.',
  tx_left_note_1: '{name} left a note on a job above.',
  tx_left_note_n: '{name} left notes on some jobs above.',
  tx_visit_off: 'This visit is off. Message {name} if you want to find another day.',
  tx_back_to_job: 'Back to the job — {n} of {m} done',
  tx_not_until: 'Not until {d}.',
  tx_start_on_day: 'You can start the job on the day.',
  tx_has_started: '{name} has started.',
  tx_ticked_so_far: '{n} of {m} jobs ticked off so far.',
  tx_change_visit_only: 'Change this visit only',
  tx_change_visit: 'Change this visit',
  tx_cancel_visit_q: 'Cancel this visit with {name}? They will see it straight away.',
  tx_cancel_booking_q: 'Cancel this booking with {name}? They will see it straight away.',
  tx_yes_cancel: 'Yes, cancel it',
  tx_keep_it: 'Keep it',
  tx_x_wrote: '{name} wrote',
  tx_waiting_on: 'Waiting on {name}.',
  tx_woman: 'Woman',
  tx_man: 'Man',
  tx_team_of: 'Team of {n}',
  tx_repeat_homes: '{n} repeat homes',
  tx_next_avail: 'Next available:',
  tx_findable_avail: 'Homes can find you on the days your calendar says you are available.',
  tx_no_common_lang: 'You have no language in common. Messages can be translated, but think about whether that works for the job.',
  tx_new_here: 'New here',
  tx_cal_reports_1: '{n} calendar report.',
  tx_cal_reports_n: '{n} calendar reports.',
  tx_cal_reports_more: 'Three reports and a profile stops appearing in search until the calendar is fixed.',
  tx_total: '{n} total',
  tx_invited_see: 'Invited — see the job',
  tx_applied_see: '{name} applied — see the job',
  tx_post_first: 'Post a job first — then you can invite {name} to it.',
  tx_which_job: 'Which job?',
  tx_recommend: 'Recommend {name} to someone',
  tx_dont_show: 'Do not show me {name} again',
  tx_name: 'Name',
  tx_a_woman: 'A woman',
  tx_a_man: 'A man',
  tx_zip_hint: 'Your own postal code, or wherever you set out from. Ozeret never asks your phone where you are — you would stop seeing Monday\'s work every time you sat at home on a Sunday.',
  tx_n_miles: '{n} miles',
  tx_further_hint: 'Homes further out than this are not shown to you, and you are not shown to them.',
  tx_areas_hint: 'Towns you know well, near where you set out from. These help homes recognise you; the postal code above is what decides how far you are shown.',
  tx_find_town: 'Find a town within {n} miles',
  tx_no_town: 'Nothing called that within {n} miles. Travel further and it may appear.',
  tx_track_posted: 'Posted',
  tx_track_chosen: 'Chosen',
  tx_track_underway: 'Underway',
  tx_track_done: 'Done',
  tx_track_reviewed: 'Reviewed',
  tx_n_countered: '{name} countered at {rate}/hr',
  tx_n_applied: '{name} applied to your {d} request',
  tx_tell_like: 'Tell people what {name} was like',
  tx_they: 'them',
  tx_homes_near_1: '1 home looking near you',
  tx_homes_near_n: '{n} homes looking near you',
  tx_a_home: 'A home',
  tx_invited_for: '{name} invited you for {d}',
  tx_booked_for: '{name} booked you for {d}',
  tx_review_waiting: 'A review is waiting to be written',
  tx_from_ozeret: 'From Ozeret:',
  tx_s_notshowing: 'That job is not showing for you -- it may be outside the areas you work.',
  tx_s_sent: 'Sent. They will see your profile and reviews.',
  tx_s_accepted: 'Accepted. They will confirm the booking.',
  tx_s_declined: 'Declined',
  tx_s_withdrawn: 'Withdrawn',
  tx_s_booked: 'Booked. Your address has been shared with them.',
  tx_s_code: 'That code did not work.',
  tx_s_deleted: 'Your account has been deleted.',
  tx_s_timeoff: 'Time off saved.',
  tx_s_week: 'Your usual week is saved.',
  tx_s_hidden: 'Hidden. Homes cannot find you.',
  tx_s_visible: 'Visible again.',
  tx_s_price: 'Price sent. If they accept it becomes the record.',
  tx_s_started: 'Started. Tick jobs off as you go.',
  tx_s_back: 'Back in your list.',
  tx_s_paid: 'Marked as paid.',
  tx_s_unpaid: 'No longer marked paid.',
  tx_s_cancelled: 'Cancelled. We have told them in your chat.',
  tx_s_finished: 'Marked finished. You can review each other now.',
  tx_s_review: 'Saved. It stays hidden until they write theirs.',
  tx_s_profile: 'Profile saved.',
  tx_s_thanks: 'Sent. Thank you — genuinely.',
  tx_s_notsent: 'Could not send that. Try again in a moment.',
  tx_s_report: 'Recorded. We look at patterns across homes, never one report.',
  tx_s_reportfail: 'Could not send that report.',
  tx_s_link: 'Link copied.',
  tx_s_nolink: 'Could not make a link.',
  tx_s_nochange: 'Could not change that just now.',
  tx_s_once: 'Saved for this visit only. They have been told.',
  tx_s_offer: 'Offer sent at {rate}/hr',
  tx_s_agreed: 'Agreed at {rate}/hr',
  tx_s_days_1: '1 day updated',
  tx_s_days_n: '{n} days updated',
  tx_yesterday: 'Yesterday',
  tx_nearby: 'nearby',
  tx_few_minutes: 'a few minutes away',
  tx_mile_away: '1 mile away',
  tx_miles_away: '{n} miles away',
  tx_ht_house: 'House',
  tx_ht_apartment: 'Apartment',
  tx_ht_townhouse: 'Townhouse',
  tx_ht_brownstone_floor: 'Brownstone floor',
  tx_park_driveway: 'Driveway parking',
  tx_park_street: 'Street parking',
  tx_park_garage: 'Garage parking',
  tx_park_none: 'No parking',
  tx_lang_en: 'English',
  tx_lang_es: 'Spanish',
  tx_lang_yi: 'Yiddish',
  tx_lang_he: 'Hebrew',
  tx_lang_ru: 'Russian',
  tx_lang_ht: 'Haitian Creole',
  tx_lang_pl: 'Polish',
  tx_lang_pt: 'Portuguese',
  tx_lang_fr: 'French',
  tx_lang_uk: 'Ukrainian',
  tx_lang_tl: 'Tagalog',
  tx_lang_sq: 'Albanian',
  tx_pay_cash: 'Cash',
  tx_pay_check: 'Check',
  tx_pay_card: 'Card in app',
  tx_trait_own_supplies: 'Brings own supplies',
  tx_trait_eco: 'Eco-friendly products',
  tx_trait_pet_ok: 'Comfortable with pets',
  tx_trait_has_car: 'Has own car',
  tx_trait_heavy_lift: 'Can do heavy lifting',
  tx_trait_organizer: 'Strong organizer',
  tx_trait_kosher_exp: 'Kosher-home experienced',
  tx_trait_no_sat: 'Does not work Saturday',
  tx_trait_no_sun: 'Does not work Sunday',
});
Object.assign(STRINGS.es, {
  tx_refreshing: 'Actualizando…',
  tx_release: 'Suelte para actualizar',
  tx_pull: 'Deslice para actualizar',
  tx_promo_hint: '¿Le dieron un código? Escríbalo aquí. Un código de lanzamiento de Ozeret añade meses gratis a su cuenta, además de lo que ya tenga. El código de un amigo le da a su amigo un mes gratis, no a usted.',
  tx_share_hint_1: 'Su propio código. Cada persona que se registre con él le da un mes gratis: no tiene que pagar nada, solo registrarse.',
  tx_share_hint_n: 'Su propio código. Cada persona que se registre con él le da {n} meses gratis: no tiene que pagar nada, solo registrarse.',
  tx_small_print: 'La letra pequeña',
  tx_small_print_hint: 'Qué es Ozeret y qué no, y exactamente qué datos guarda sobre usted.',
  tx_lang_hint: 'Cambia toda la app al momento. El texto de su perfil queda en el idioma en que lo escribió.',
  tx_never_ssn: 'Ozeret nunca pide número de seguro social, estatus migratorio ni ningún documento del gobierno.',
  tx_dim_thorough: 'Minuciosidad',
  tx_dim_reliable: 'Puntualidad',
  tx_dim_comm: 'Comunicación',
  tx_dim_care: 'Cuidado con las cosas',
  tx_dim_friendly: 'Amable y respetuosa',
  tx_dim_clear: 'Clara sobre el trabajo',
  tx_dim_paid: 'Pagó lo acordado',
  tx_dim_ready: 'La casa estaba lista',
  tx_homes_now_1: '1 casa busca ayuda ahora mismo.',
  tx_homes_now_n: '{n} casas buscan ayuda ahora mismo.',
  tx_homes_on_1: '1 casa busca ayuda el {date}.',
  tx_homes_on_n: '{n} casas buscan ayuda el {date}.',
  tx_homes_only_looking: 'Las casas aparecen aquí solo mientras están buscando.',
  tx_pair_lede: 'Un segundo trabajo el mismo día, lo bastante cerca para llegar a tiempo. Es la diferencia entre medio día y un día completo.',
  tx_open: 'Abierto',
  tx_pair_travel: 'unos {travel} · tiene {gap} entre los dos',
  tx_n_of_m: '{n} de {m}',
  tx_beds: '{n} hab.',
  tx_baths: '{n} baños',
  tx_sqft: '{n} pies²',
  tx_you_countered: 'Pidió {rate}',
  tx_they_invited: 'Le invitaron',
  tx_you_applied: 'Usted se postuló',
  tx_reach_1: '1 trabajo a su alcance.',
  tx_reach_n: '{n} trabajos a su alcance.',
  tx_map_help: 'Toque un precio para abrir ese trabajo. Si varios comparten código postal, comparten etiqueta: tóquela para verlos.',
  tx_unplaced: '{n} más todavía no han dado un código postal.',
  tx_job_gone: 'Este trabajo ya no está disponible',
  tx_job_gone_sub: 'La casa ya reservó a alguien o dejó de buscar.',
  tx_find_other: 'Buscar otro trabajo',
  tx_got_job: 'Le dieron el trabajo. Aquí está su reserva.',
  tx_rated_them: '{n} calificaciones de limpiadores',
  tx_every_ongoing: 'Cada {day}, sin fecha de fin. Al aceptar, ese horario queda reservado en su calendario hasta que cualquiera de los dos lo termine.',
  tx_est_off: 'Para esta lista en una casa de este tamaño calculamos unas {s} horas. Pidieron {h}. Conviene acordar qué es prioritario antes de empezar.',
  tx_est_ok: 'Calculamos unas {s} horas para esta lista, cerca de las {h} que pidieron.',
  tx_supplies_yes: 'Ponen los productos',
  tx_supplies_no: 'Lleve sus productos',
  tx_no_pets: 'Sin mascotas',
  tx_kids: '{n} niños',
  tx_no_kids: 'Sin niños',
  tx_elevator: 'Ascensor',
  tx_stairs: 'Escaleras',
  tx_ground: 'Planta baja',
  tx_x_invited_you: '{name} le invitó',
  tx_accept_lede: 'Acepte para decirles que puede ir. Ellos confirman la reserva.',
  tx_accept: 'Aceptar',
  tx_decline: 'Rechazar',
  tx_you_offered: 'Ofreció {rate}',
  tx_jobs_1: '1 trabajo',
  tx_jobs_n: '{n} trabajos',
  tx_nothing_booked: 'Nada reservado',
  tx_still_avail: 'Todavía libre:',
  tx_nothing_more_today: 'nada más hoy',
  tx_until_next: '{d} hasta el siguiente',
  tx_week_of: 'Semana del {d}',
  tx_pct_booked: '{n}% reservado',
  tx_still_open: 'Libre {n} h',
  tx_unsold: '{n} horas sin reservar esta semana.',
  tx_unsold_more: 'Las casas filtran por fecha: cuanto más exacto sea su calendario, más de estas horas se llenan solas.',
  tx_tapdays_more: 'Elija tantos como quiera y márquelos disponibles, no disponibles, o bloquee ciertas horas. Los trabajos confirmados se bloquean solos.',
  tx_earlier_months: 'Meses anteriores',
  tx_later_months: 'Meses siguientes',
  tx_this_month: 'Este mes',
  tx_blocked: 'Bloqueado',
  tx_not_working: 'No trabaja',
  tx_off: 'Descanso',
  tx_every_other: 'Cada dos semanas, {day}',
  tx_since: 'Desde {d}',
  tx_hidden: 'Está oculto/a. Las casas no le encuentran.',
  tx_findable_cal: 'Las casas le encuentran los días que su calendario dice que está libre.',
  tx_cal_confirmed: 'Confirmado el {d}',
  tx_default_week: 'Este es su horario habitual. Lo que cambie en un día concreto del calendario tiene prioridad.',
  tx_n_of_m_done: '{n} de {m} hechos',
  tx_finish_without: 'Puede terminar sin marcar todo: diga qué pasó con lo que no pudo hacer, y la casa lo verá.',
  tx_mark_not_done: 'Marcar como no hecho',
  tx_mark_done: 'Marcar como hecho',
  tx_edit_said: 'Editar lo que dijo',
  tx_say_something: 'Decir algo sobre este trabajo',
  tx_paid: 'Pagado',
  tx_not_paid: 'No pagado',
  tx_confirmed: 'Confirmado',
  tx_cancelled: 'Cancelado',
  tx_not_marked_paid: 'Sin marcar como pagado',
  tx_you_not_marked_paid: 'No ha marcado como pagado',
  tx_waiting_back: 'Esperando respuesta',
  tx_others_applied_1: '1 persona más se postuló',
  tx_others_applied_n: '{n} personas más se postularon',
  tx_only_you: 'Solo usted por ahora',
  tx_address: 'Dirección',
  tx_you: 'Usted',
  tx_marked_paid: '{who} lo marcó como pagado el {d}',
  tx_tick_paid_home: 'Márquelo cuando haya pagado, para que los dos vean lo pendiente.',
  tx_tick_paid_cleaner: 'Márquelo cuando le hayan pagado.',
  tx_left_note_1: '{name} dejó una nota en un trabajo de arriba.',
  tx_left_note_n: '{name} dejó notas en algunos trabajos de arriba.',
  tx_visit_off: 'Esta visita no va. Escriba a {name} si quiere buscar otro día.',
  tx_back_to_job: 'Volver al trabajo: {n} de {m} hechos',
  tx_not_until: 'No hasta el {d}.',
  tx_start_on_day: 'Podrá empezar el trabajo ese día.',
  tx_has_started: '{name} ya empezó.',
  tx_ticked_so_far: '{n} de {m} tareas hechas hasta ahora.',
  tx_change_visit_only: 'Cambiar solo esta visita',
  tx_change_visit: 'Cambiar esta visita',
  tx_cancel_visit_q: '¿Cancelar esta visita con {name}? Lo verá enseguida.',
  tx_cancel_booking_q: '¿Cancelar esta reserva con {name}? Lo verá enseguida.',
  tx_yes_cancel: 'Sí, cancelar',
  tx_keep_it: 'Mantenerla',
  tx_x_wrote: '{name} escribió',
  tx_waiting_on: 'Esperando a {name}.',
  tx_woman: 'Mujer',
  tx_man: 'Hombre',
  tx_team_of: 'Equipo de {n}',
  tx_repeat_homes: '{n} casas que repiten',
  tx_next_avail: 'Próximo día libre:',
  tx_findable_avail: 'Las casas pueden encontrarle los días que su calendario dice que está disponible.',
  tx_no_common_lang: 'No tienen ningún idioma en común. Los mensajes se pueden traducir, pero piense si eso funciona para el trabajo.',
  tx_new_here: 'Nuevo/a aquí',
  tx_cal_reports_1: '{n} aviso sobre el calendario.',
  tx_cal_reports_n: '{n} avisos sobre el calendario.',
  tx_cal_reports_more: 'Con tres avisos, el perfil deja de aparecer en las búsquedas hasta que se corrija el calendario.',
  tx_total: '{n} en total',
  tx_invited_see: 'Invitado/a: ver el trabajo',
  tx_applied_see: '{name} se postuló: ver el trabajo',
  tx_post_first: 'Publique un trabajo primero; luego podrá invitar a {name}.',
  tx_which_job: '¿Qué trabajo?',
  tx_recommend: 'Recomendar a {name}',
  tx_dont_show: 'No volver a mostrarme a {name}',
  tx_name: 'Nombre',
  tx_a_woman: 'Una mujer',
  tx_a_man: 'Un hombre',
  tx_zip_hint: 'Su propio código postal, o el lugar desde donde sale. Ozeret nunca le pregunta al teléfono dónde está: dejaría de ver el trabajo del lunes cada vez que está en casa un domingo.',
  tx_n_miles: '{n} millas',
  tx_further_hint: 'Las casas más lejos no se le muestran a usted, ni usted a ellas.',
  tx_areas_hint: 'Pueblos que conoce bien, cerca de donde sale. Ayudan a las casas a reconocerle; lo que decide a qué distancia se le muestra es el código postal de arriba.',
  tx_find_town: 'Buscar un pueblo a menos de {n} millas',
  tx_no_town: 'No hay nada con ese nombre a menos de {n} millas. Si viaja más lejos, puede aparecer.',
  tx_track_posted: 'Publicado',
  tx_track_chosen: 'Elegido',
  tx_track_underway: 'En curso',
  tx_track_done: 'Hecho',
  tx_track_reviewed: 'Reseñado',
  tx_n_countered: '{name} pidió {rate}/h',
  tx_n_applied: '{name} se postuló a su pedido del {d}',
  tx_tell_like: 'Cuente cómo fue con {name}',
  tx_they: 'ellos',
  tx_homes_near_1: '1 casa busca ayuda cerca de usted',
  tx_homes_near_n: '{n} casas buscan ayuda cerca de usted',
  tx_a_home: 'Una casa',
  tx_invited_for: '{name} le invitó para el {d}',
  tx_booked_for: '{name} le reservó para el {d}',
  tx_review_waiting: 'Tiene una reseña pendiente de escribir',
  tx_from_ozeret: 'De Ozeret:',
  tx_s_notshowing: 'Ese trabajo no le aparece: puede estar fuera de las zonas donde trabaja.',
  tx_s_sent: 'Enviado. Verán su perfil y sus reseñas.',
  tx_s_accepted: 'Aceptado. Ellos confirmarán la reserva.',
  tx_s_declined: 'Rechazado',
  tx_s_withdrawn: 'Retirado',
  tx_s_booked: 'Reservado. Se le ha compartido su dirección.',
  tx_s_code: 'Ese código no funcionó.',
  tx_s_deleted: 'Su cuenta se ha borrado.',
  tx_s_timeoff: 'Días libres guardados.',
  tx_s_week: 'Su semana habitual está guardada.',
  tx_s_hidden: 'Oculto/a. Las casas no le encuentran.',
  tx_s_visible: 'Visible de nuevo.',
  tx_s_price: 'Precio enviado. Si lo aceptan, queda como acordado.',
  tx_s_started: 'Empezado. Marque las tareas a medida que las haga.',
  tx_s_back: 'De vuelta en su lista.',
  tx_s_paid: 'Marcado como pagado.',
  tx_s_unpaid: 'Ya no está marcado como pagado.',
  tx_s_cancelled: 'Cancelado. Se lo dijimos en su chat.',
  tx_s_finished: 'Marcado como terminado. Ya pueden dejarse reseñas.',
  tx_s_review: 'Guardada. Queda oculta hasta que escriban la suya.',
  tx_s_profile: 'Perfil guardado.',
  tx_s_thanks: 'Enviado. Gracias, de verdad.',
  tx_s_notsent: 'No se pudo enviar. Inténtelo en un momento.',
  tx_s_report: 'Registrado. Miramos lo que pasa con varias casas, nunca un solo aviso.',
  tx_s_reportfail: 'No se pudo enviar ese aviso.',
  tx_s_link: 'Enlace copiado.',
  tx_s_nolink: 'No se pudo crear el enlace.',
  tx_s_nochange: 'No se pudo cambiar ahora mismo.',
  tx_s_once: 'Guardado solo para esta visita. Ya se lo dijimos.',
  tx_s_offer: 'Oferta enviada a {rate}/h',
  tx_s_agreed: 'Acordado a {rate}/h',
  tx_s_days_1: '1 día actualizado',
  tx_s_days_n: '{n} días actualizados',
  tx_yesterday: 'Ayer',
  tx_nearby: 'cerca',
  tx_few_minutes: 'a pocos minutos',
  tx_mile_away: 'a 1 milla',
  tx_miles_away: 'a {n} millas',
  tx_ht_house: 'Casa',
  tx_ht_apartment: 'Apartamento',
  tx_ht_townhouse: 'Casa adosada',
  tx_ht_brownstone_floor: 'Piso en brownstone',
  tx_park_driveway: 'Estacionamiento en la entrada',
  tx_park_street: 'Estacionamiento en la calle',
  tx_park_garage: 'Estacionamiento en garaje',
  tx_park_none: 'Sin estacionamiento',
  tx_lang_en: 'Inglés',
  tx_lang_es: 'Español',
  tx_lang_yi: 'Ídish',
  tx_lang_he: 'Hebreo',
  tx_lang_ru: 'Ruso',
  tx_lang_ht: 'Criollo haitiano',
  tx_lang_pl: 'Polaco',
  tx_lang_pt: 'Portugués',
  tx_lang_fr: 'Francés',
  tx_lang_uk: 'Ucraniano',
  tx_lang_tl: 'Tagalo',
  tx_lang_sq: 'Albanés',
  tx_pay_cash: 'Efectivo',
  tx_pay_check: 'Cheque',
  tx_pay_card: 'Tarjeta en la app',
  tx_trait_own_supplies: 'Lleva sus productos',
  tx_trait_eco: 'Productos ecológicos',
  tx_trait_pet_ok: 'Cómodo/a con mascotas',
  tx_trait_has_car: 'Tiene carro propio',
  tx_trait_heavy_lift: 'Puede levantar peso',
  tx_trait_organizer: 'Muy organizado/a',
  tx_trait_kosher_exp: 'Experiencia en casas kosher',
  tx_trait_no_sat: 'No trabaja el sábado',
  tx_trait_no_sun: 'No trabaja el domingo',
});
